
import React, { useState, useRef, useEffect, useMemo, useLayoutEffect, useCallback } from "react";
import { useSelector, useDispatch, useStore } from "react-redux";
import {
    addBlockToScript,
    clearScript,
    pushUndoState,
    updateBlockCount,
    addBlockToSimulatorScript,
    clearSimulatorScript,
    updateSimulatorBlockCount,
    setCameraState,
    updateBlockMessage,
    updateBlockCustomData,
    updateBlockColor,
    updateSimulatorBlockColor,
    updateSimulatorBlockMessage,
    addBlockToContainer,
    removeBlockFromContainer,
    moveBlockBetweenContainers,
    updateBlockPosition,
    connectBlocks,
    disconnectBlock,
} from "../../store/sceneSlice";
import NumberPicker from "./NumberPicker";
import "../../css/ScriptArea.css";
import { run } from "../../utils/runScript";
import PinConfigModal from "../ui/PinConfigModal";
import ColorPickerPopover from "./ColorPickerPopover";
import LanguagePickerPopover from "./LanguagePickerPopover";
import TextMessageModal from "./TextMessageModal";
import {
    HAND_FINGER_OPTIONS,
    DEFAULT_FINGER,
    normalizeFinger
} from "../../utils/handLandmarks";
import { startTouchDrag, TOUCH_DROP_EVENT } from "../../utils/touchDragDrop";
import { DEFAULT_MESSAGE_COLOR, MESSAGE_COLOR_OPTIONS, getMessageIconPath, normalizeMessageColor } from "../../constants/messageBlocks";
import { findStartBlockIds } from "../../utils/scriptStarts";
import textToSpeechManager, { getAvailableTTSLanguages } from "../../utils/TextToSpeechManager";

const DEFAULT_SCRIPTAREA_WIDTH = 1000;
const DEFAULT_SCRIPTAREA_HEIGHT = 600;
const ROW_HEIGHT = 90;
const MIN_START_X = 20;

const clampValue = (value, min, max) => Math.max(min, Math.min(max, value));
const getViewportDimension = (value, fallback) => Math.max(1, Number.isFinite(value) ? value : fallback);
const hasStoredPosition = (block) => Number.isFinite(block?.x) || Number.isFinite(block?.y);
const blockNeedsNormalization = (block) => {
    if (!block || !hasStoredPosition(block)) return false;
    const needsXMeta = Number.isFinite(block?.x) && (!Number.isFinite(block?.layoutRefWidth) || block.layoutRefWidth <= 0 || !Number.isFinite(block?.xRatio));
    const needsYMeta = Number.isFinite(block?.y) && (!Number.isFinite(block?.layoutRefHeight) || block.layoutRefHeight <= 0 || !Number.isFinite(block?.yRatio));
    return needsXMeta || needsYMeta;
};

function resolveRelativeBlockPosition(block, viewportWidth, viewportHeight) {
    if (!block) return null;
    const width = getViewportDimension(viewportWidth, DEFAULT_SCRIPTAREA_WIDTH);
    const height = getViewportDimension(viewportHeight, DEFAULT_SCRIPTAREA_HEIGHT);
    const blockWidth = getBlockWidthForSnap(block);
    const maxX = Math.max(MIN_START_X, width - blockWidth);
    const ratioX = Number.isFinite(block?.xRatio)
        ? block.xRatio
        : Number.isFinite(block?.layoutRefWidth) && block.layoutRefWidth > 0 && Number.isFinite(block?.x)
            ? block.x / block.layoutRefWidth
            : null;
    const ratioY = Number.isFinite(block?.yRatio)
        ? block.yRatio
        : Number.isFinite(block?.layoutRefHeight) && block.layoutRefHeight > 0 && Number.isFinite(block?.y)
            ? block.y / block.layoutRefHeight
            : null;

    const resolvedX = Number.isFinite(ratioX)
        ? clampValue(ratioX * width, MIN_START_X, maxX)
        : Number.isFinite(block?.x)
            ? clampValue(block.x, MIN_START_X, maxX)
            : null;
    const maxY = Math.max(0, height - ROW_HEIGHT);
    const resolvedY = Number.isFinite(ratioY)
        ? clampValue(ratioY * height, 0, maxY)
        : Number.isFinite(block?.y)
            ? clampValue(block.y, 0, maxY)
            : null;

    if (!Number.isFinite(resolvedX) && !Number.isFinite(resolvedY)) {
        return null;
    }

    return {
        x: Number.isFinite(resolvedX) ? resolvedX : MIN_START_X,
        y: Number.isFinite(resolvedY) ? resolvedY : 50,
    };
}

// Repeat shell drawing constants (ScratchJr 9-slice)
const REPEAT_BASE_WIDTH = 176;
const REPEAT_BASE_HEIGHT = 82;
const REPEAT_MIN_WIDTH = 176;
const REPEAT_SLICE_LEFT = 92;
const REPEAT_SLICE_MID = 1;
const REPEAT_SLICE_TOP = 29;
const REPEAT_SLICE_BOTTOM = 53;
const REPEAT_BOTTOM_LEFT_WIDTH = 45;
const REPEAT_CONTENT_PADDING_LEFT = 46;
const REPEAT_CONTENT_PADDING_RIGHT = 54;
const REPEAT_CONTENT_PADDING_TOP = 17;
const REPEAT_CONTENT_PADDING_BOTTOM = 1;
const REPEAT_CHILD_OVERLAP = 11; // Visual overlap between nested blocks.
const REPEAT_SHELL_IMAGE = "./assets/blocks/repeat.png";
const REPEAT_SHELL_SHADOW_IMAGE = "./assets/blocks/shadowRepeat.png";

const loadImage = (src) => new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
});

const drawRepeatShellImage = (canvas, img, targetWidth, targetHeight) => {
    if (!canvas || !img) return;
    const dpr = window.devicePixelRatio || 1;
    const hrubberband = Math.max(0, Math.round(targetWidth - REPEAT_BASE_WIDTH));
    const vrubberband = Math.max(0, Math.round(targetHeight - REPEAT_BASE_HEIGHT));
    const rightWidth = Math.max(0, img.width - (REPEAT_SLICE_LEFT + REPEAT_SLICE_MID));
    const width = REPEAT_BASE_WIDTH + hrubberband;
    const height = REPEAT_BASE_HEIGHT + vrubberband;
    canvas.width = Math.max(1, Math.round(width * dpr));
    canvas.height = Math.max(1, Math.round(height * dpr));
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const scale = dpr;

    // Top row
    ctx.drawImage(img, 0, 0, REPEAT_SLICE_LEFT, REPEAT_SLICE_TOP, 0, 0, REPEAT_SLICE_LEFT * scale, REPEAT_SLICE_TOP * scale);
    if (hrubberband > 0) {
        ctx.drawImage(
            img,
            REPEAT_SLICE_LEFT,
            0,
            REPEAT_SLICE_MID,
            REPEAT_SLICE_TOP,
            REPEAT_SLICE_LEFT * scale,
            0,
            hrubberband * scale,
            REPEAT_SLICE_TOP * scale
        );
    }
    ctx.drawImage(
        img,
        REPEAT_SLICE_LEFT + REPEAT_SLICE_MID,
        0,
        rightWidth,
        REPEAT_SLICE_TOP,
        (REPEAT_SLICE_LEFT + hrubberband) * scale,
        0,
        rightWidth * scale,
        REPEAT_SLICE_TOP * scale
    );

    // Vertical stretch (sides)
    if (vrubberband > 0) {
        ctx.drawImage(
            img,
            0,
            REPEAT_SLICE_TOP,
            REPEAT_SLICE_LEFT,
            REPEAT_SLICE_MID,
            0,
            REPEAT_SLICE_TOP * scale,
            REPEAT_SLICE_LEFT * scale,
            vrubberband * scale
        );
        ctx.drawImage(
            img,
            REPEAT_SLICE_LEFT + REPEAT_SLICE_MID,
            REPEAT_SLICE_TOP,
            rightWidth,
            REPEAT_SLICE_MID,
            (REPEAT_SLICE_LEFT + hrubberband) * scale,
            REPEAT_SLICE_TOP * scale,
            rightWidth * scale,
            vrubberband * scale
        );
    }

    // Bottom row (leave the interior gap open)
    ctx.drawImage(
        img,
        0,
        REPEAT_SLICE_TOP,
        REPEAT_BOTTOM_LEFT_WIDTH,
        REPEAT_SLICE_BOTTOM,
        0,
        (REPEAT_SLICE_TOP + vrubberband) * scale,
        REPEAT_BOTTOM_LEFT_WIDTH * scale,
        REPEAT_SLICE_BOTTOM * scale
    );
    ctx.drawImage(
        img,
        REPEAT_SLICE_LEFT + REPEAT_SLICE_MID,
        REPEAT_SLICE_TOP,
        rightWidth,
        REPEAT_SLICE_BOTTOM,
        (REPEAT_SLICE_LEFT + hrubberband) * scale,
        (REPEAT_SLICE_TOP + vrubberband) * scale,
        rightWidth * scale,
        REPEAT_SLICE_BOTTOM * scale
    );
};

const RepeatImageShell = ({
    children,
    placeholder = "Drop blocks here",
    isDraggingOver,
    onDragOver,
    onDragLeave,
    onDrop,
    renderCountBadge,
    resolvedBlockIcon,
    blockId,
    contentWidthOverride,
}) => {
    const [shellImages, setShellImages] = useState(null);
    const canvasRef = useRef(null);
    const shadowRef = useRef(null);
    const childCount = React.Children.count(children);

    useEffect(() => {
        let cancelled = false;
        const loadImages = async () => {
            const [base, shadow] = await Promise.all([
                loadImage(REPEAT_SHELL_IMAGE),
                loadImage(REPEAT_SHELL_SHADOW_IMAGE),
            ]);
            if (!cancelled) {
                setShellImages({ base, shadow });
            }
        };
        loadImages();
        return () => {
            cancelled = true;
        };
    }, []);

    const resolvedContentWidth = Number.isFinite(contentWidthOverride)
        ? contentWidthOverride
        : 0;
    const totalWidth = Math.max(
        REPEAT_MIN_WIDTH,
        resolvedContentWidth + REPEAT_CONTENT_PADDING_LEFT + REPEAT_CONTENT_PADDING_RIGHT
    );
    const renderedChildren = childCount > 0 ? children : null;

    useLayoutEffect(() => {
        if (!shellImages?.base) return;
        drawRepeatShellImage(canvasRef.current, shellImages.base, totalWidth, REPEAT_BASE_HEIGHT);
        if (shellImages.shadow) {
            drawRepeatShellImage(shadowRef.current, shellImages.shadow, totalWidth, REPEAT_BASE_HEIGHT);
        }
    }, [shellImages, totalWidth]);

    return (
        <div
            className={`repeat-shell ${isDraggingOver ? "drag-over" : ""}`}
            style={{ width: `${totalWidth}px`, height: `${REPEAT_BASE_HEIGHT}px` }}
            data-touch-drop-target="repeat-shell"
        >
            <canvas className="repeat-shell-shadow" ref={shadowRef} aria-hidden="true" />
            <canvas className="repeat-shell-canvas" ref={canvasRef} aria-hidden="true" />
            {resolvedBlockIcon && (
                <div className="repeat-shell-icon">
                    <img src={resolvedBlockIcon} alt="" draggable={false} />
                </div>
            )}
            <div className="repeat-shell-badge">
                {typeof renderCountBadge === "function" ? renderCountBadge() : null}
            </div>
            <div
                className="repeat-shell-content"
                style={{
                    paddingLeft: `${REPEAT_CONTENT_PADDING_LEFT}px`,
                    paddingRight: `${REPEAT_CONTENT_PADDING_RIGHT}px`,
                    paddingTop: `${REPEAT_CONTENT_PADDING_TOP}px`,
                    paddingBottom: `${REPEAT_CONTENT_PADDING_BOTTOM}px`,
                }}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
            >
                <div className="repeat-shell-measure">
                    {renderedChildren || <div className="repeat-empty-hint">{placeholder}</div>}
                </div>
            </div>
        </div>
    );
};

// ========== RECURSIVE BLOCK RENDERER COMPONENT ==========
// Add this BEFORE "export default function ScriptArea"

const MESSAGE_BLOCK_TYPES = new Set(["startOnMessage", "sendMessage"]);

const isFixedStartBlock = (block) => block?.category === "start" && block?.type !== "sendMessage";
const isEndBlock = (block) => block?.category === "end";
const isMessageBlock = (block) => MESSAGE_BLOCK_TYPES.has(block?.type);

const resolveBlockIcon = (block) => {
    if (isMessageBlock(block)) {
        return getMessageIconPath(block.type, block.messageColor || DEFAULT_MESSAGE_COLOR);
    }
    return block?.icon;
};

function getBlockRenderWidth(block) {
    if (!block) return BLOCK_RENDER_WIDTH;
    if (block.isContainer || block.type === "repeat") {
        return getRepeatBlockWidth(block);
    }
    return isFixedStartBlock(block) ? START_BLOCK_RENDER_WIDTH : BLOCK_RENDER_WIDTH;
}

function getBlockSnapInsets(block) {
    if (!block) {
        return { left: 0, right: BLOCK_OVERLAP };
    }
    if (block.isContainer || block.type === "repeat") {
        return { left: REPEAT_LEFT_SNAP_INSET, right: REPEAT_RIGHT_SNAP_INSET };
    }
    return { left: 0, right: BLOCK_OVERLAP };
}

function getBlockLeftSnapInset(block) {
    return getBlockSnapInsets(block).left;
}

function getBlockRightSnapOffset(block) {
    const width = getBlockRenderWidth(block);
    return Math.max(0, width - getBlockSnapInsets(block).right);
}

function getBlockLeftConnectorOffsetY(block) {
    if (!block) return NORMAL_CONNECTOR_Y;
    if (block.isContainer || block.type === "repeat") {
        return REPEAT_LEFT_CONNECTOR_Y;
    }
    return NORMAL_CONNECTOR_Y;
}

function getBlockRightConnectorOffsetY(block) {
    if (!block) return NORMAL_CONNECTOR_Y;
    if (block.isContainer || block.type === "repeat") {
        return REPEAT_RIGHT_CONNECTOR_Y;
    }
    return NORMAL_CONNECTOR_Y;
}

function getRepeatInnerContentWidth(block) {
    if (!block?.children || block.children.length === 0) return 0;
    const total = block.children.reduce((sum, child) => sum + getBlockRenderWidth(child), 0);
    return Math.max(0, total - REPEAT_CHILD_OVERLAP * (block.children.length - 1));
}

function getRepeatBlockWidth(block) {
    const innerWidth = getRepeatInnerContentWidth(block);
    return Math.max(
        REPEAT_MIN_WIDTH,
        innerWidth + REPEAT_CONTENT_PADDING_LEFT + REPEAT_CONTENT_PADDING_RIGHT
    );
}

function BlockRenderer({
    block,
    index,
    parentContainerId = "main",
    editableTarget,
    inlineEdit,
    dispatch,
    histPayload,
    clickBlock,
    handleBadgeClick,
    handleBadgeDoubleClick,
    handleBlockDragStart,
    handleBlockDragEnd,
    handleBlockTouchStart,
    handleBlockTouchMove,
    handleBlockTouchEnd,
    renderInlineNumberInput,
    renderInlineTextInput,
    globalCameraState,
    isScriptRunning,
    handleRunClick,
    openColorPicker,
    handlePinBlockClick,
    resolveBlockPosition,
    draggedBlock,
    dragPreviewActive,
    dragPreviewIdSet,
    dragPreviewReady,
    languageOptions,
}) {
    const [isDraggingOver, setIsDraggingOver] = useState(false);

    // Check if this is a container block (Repeat)
    const isContainer = block.isContainer || block.type === "repeat";
    const repeatContentWidth = isContainer ? getRepeatInnerContentWidth(block) : 0;
    const resolvedBlockIcon = resolveBlockIcon(block);
    const isTopLevel = parentContainerId === "main";
    const resolvedPosition = isTopLevel && typeof resolveBlockPosition === "function"
        ? resolveBlockPosition(block)
        : { x: 0, y: 0 };
    const containerStyle = isTopLevel
        ? {
            position: "absolute",
            left: `${resolvedPosition?.x || 0}px`,
            top: `${resolvedPosition?.y || 0}px`,
            zIndex: 10,
        }
        : undefined;
    const previewHiddenClass = isTopLevel && dragPreviewActive && dragPreviewIdSet?.has(block.id) && dragPreviewReady ? "drag-hidden" : "";

    // Container drag handlers
    const handleContainerDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDraggingOver(true);
    };

    const handleContainerDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDraggingOver(false);
    };

    const getContainerDropIndex = (event, excludeBlockId = null) => {
        const content = event.currentTarget;
        const measure = content?.querySelector(".repeat-shell-measure");
        const listRoot = measure?.querySelector(".repeat-nested-blocks") || measure;
        if (!listRoot) {
            return block.children?.length || 0;
        }

        const blockNodes = Array.from(listRoot.children).filter(
            (node) => node?.dataset?.blockId
        );

        if (!blockNodes.length) {
            return 0;
        }

        const dropX = event.clientX;
        let insertIndex = blockNodes.length;
        for (let i = 0; i < blockNodes.length; i++) {
            const rect = blockNodes[i].getBoundingClientRect();
            const centerX = rect.left + rect.width / 2;
            if (dropX < centerX) {
                insertIndex = i;
                break;
            }
        }

        if (excludeBlockId) {
            const originalIndex = blockNodes.findIndex(
                (node) => node.dataset.blockId === excludeBlockId
            );
            if (originalIndex !== -1 && insertIndex > originalIndex) {
                insertIndex -= 1;
            }
        }

        return Math.max(0, insertIndex);
    };

    const handleContainerDrop = (e) => {
        const rawNew = e.dataTransfer.getData("application/block");
        const rawExisting = e.dataTransfer.getData("application/script-block");
        let existingPayload = null;

        if (rawExisting) {
            try {
                existingPayload = JSON.parse(rawExisting);
            } catch (err) {
                console.error("Drop payload parse error:", err);
            }
        }

        if (!rawNew && !rawExisting) {
            return;
        }

        if (existingPayload && existingPayload.blockId === block.id) {
            // Let the script-area drop handler reposition the repeat block.
            setIsDraggingOver(false);
            return;
        }

        if (rawExisting && !existingPayload && !rawNew) {
            return;
        }

        e.preventDefault();
        e.stopPropagation();
        setIsDraggingOver(false);

        if (rawNew) {
            // Dropping a new block from palette INTO the repeat container
            try {
                const newBlock = JSON.parse(rawNew);

                // Don't allow start/end blocks inside containers
                if (newBlock.category === "start" || newBlock.category === "end") {
                    console.warn("Cannot add start/end blocks inside containers");
                    return;
                }

                dispatch(pushUndoState(histPayload));
                const insertIndex = getContainerDropIndex(e);

                if (editableTarget.type === "simulatorRobot") {
                    dispatch(addBlockToContainer({
                        robotId: editableTarget.id,
                        containerId: block.id,
                        block: newBlock,
                        index: insertIndex,
                    }));
                } else {
                    dispatch(addBlockToContainer({
                        actorId: editableTarget.id,
                        containerId: block.id,
                        block: newBlock,
                        index: insertIndex,
                    }));
                }
            } catch (err) {
                console.error("Error dropping block into container:", err);
            }
        } else if (rawExisting && existingPayload) {
            // Moving an existing block INTO the container
            try {
                const payload = existingPayload;

                dispatch(pushUndoState(histPayload));
                const insertIndex = getContainerDropIndex(e, payload.blockId);

                if (editableTarget.type === "simulatorRobot") {
                    dispatch(moveBlockBetweenContainers({
                        robotId: editableTarget.id,
                        fromContainerId: payload.parentContainerId || "main",
                        toContainerId: block.id,
                        blockId: payload.blockId,
                        toIndex: insertIndex,
                    }));
                } else {
                    dispatch(moveBlockBetweenContainers({
                        actorId: editableTarget.id,
                        fromContainerId: payload.parentContainerId || "main",
                        toContainerId: block.id,
                        blockId: payload.blockId,
                        toIndex: insertIndex,
                    }));
                }
            } catch (err) {
                console.error("Error moving block into container:", err);
            }
        }
    };

    // Render count badge for repeat block
    const renderCountBadge = () => {
        if (!isContainer) return null;

        const value = typeof block.count === "number" ? block.count : 4;

        if (inlineEdit?.blockId === block.id && inlineEdit?.mode === "number") {
            return renderInlineNumberInput(block);
        }

        const repeatBadgeStyle = {
            backgroundImage: `url(${process.env.PUBLIC_URL}/assets/misc/Number-01.png)`,
        };

        return (
            <button
                type="button"
                className="block-count-badge repeat-count-badge"
                style={repeatBadgeStyle}
                onClick={(e) => handleBadgeClick(e, block)}
                onDoubleClick={(e) => handleBadgeDoubleClick(e, block, "number", value)}
                onTouchEnd={(e) => handleBadgeClick(e, block)}
            >
                {value}
            </button>
        );
    };

    const nestedChildren = block.children && block.children.length > 0 ? (
        <div className="repeat-nested-blocks">
            {block.children.map((childBlock, childIndex) => {
                const ChildRenderer = (childBlock.isContainer || childBlock.type === "repeat")
                    ? BlockRenderer
                    : ScriptBlock;
                return (
                    <ChildRenderer
                        key={childBlock.id}
                        block={childBlock}
                        index={childIndex}
                        parentContainerId={block.id}
                        editableTarget={editableTarget}
                        inlineEdit={inlineEdit}
                        dispatch={dispatch}
                        histPayload={histPayload}
                        clickBlock={clickBlock}
                        handleBadgeClick={handleBadgeClick}
                        handleBadgeDoubleClick={handleBadgeDoubleClick}
                        handleBlockDragStart={handleBlockDragStart}
                        handleBlockDragEnd={handleBlockDragEnd}
                        handleBlockTouchStart={handleBlockTouchStart}
                        handleBlockTouchMove={handleBlockTouchMove}
                        handleBlockTouchEnd={handleBlockTouchEnd}
                        renderInlineNumberInput={renderInlineNumberInput}
                        renderInlineTextInput={renderInlineTextInput}
                        globalCameraState={globalCameraState}
                        isScriptRunning={isScriptRunning}
                        handleRunClick={handleRunClick}
                        openColorPicker={openColorPicker}
                        handlePinBlockClick={handlePinBlockClick}
                        resolveBlockPosition={resolveBlockPosition}
                        draggedBlock={draggedBlock}
                        dragPreviewActive={dragPreviewActive}
                        dragPreviewIdSet={dragPreviewIdSet}
                        dragPreviewReady={dragPreviewReady}
                        languageOptions={languageOptions}
                    />
                );
            })}
        </div>
    ) : null;

    // RENDER CONTAINER BLOCK (Repeat)
    if (isContainer) {
        const repeatDragHandlers = isTopLevel
            ? {
                draggable: true,
                onDragStart: (e) => {
                    if (e?.target?.closest?.(".repeat-nested-blocks")) {
                        return;
                    }
                    handleBlockDragStart?.(e, block, index, parentContainerId);
                },
                onDragEnd: (e) => {
                    if (e?.target?.closest?.(".repeat-nested-blocks")) {
                        return;
                    }
                    handleBlockDragEnd?.(e, block, index, parentContainerId);
                },
                onTouchStart: (e) => {
                    if (e?.target?.closest?.(".repeat-nested-blocks")) {
                        return;
                    }
                    handleBlockTouchStart?.(e, block, index, parentContainerId);
                },
                onTouchMove: (e) => {
                    if (e?.target?.closest?.(".repeat-nested-blocks")) {
                        return;
                    }
                    handleBlockTouchMove?.(e, block, index, parentContainerId);
                },
                onTouchEnd: (e) => {
                    if (e?.target?.closest?.(".repeat-nested-blocks")) {
                        return;
                    }
                    handleBlockTouchEnd?.(e, block, index, parentContainerId);
                },
                onTouchCancel: (e) => {
                    if (e?.target?.closest?.(".repeat-nested-blocks")) {
                        return;
                    }
                    handleBlockTouchEnd?.(e, block, index, parentContainerId);
                },
            }
            : { draggable: false };

        return (
            <div
                className={`repeat-container-special ${previewHiddenClass}`}
                data-block-id={block.id}
                style={containerStyle}
            >
                <div
                    className="repeat-block-wrapper"
                    {...repeatDragHandlers}
                    onClick={(e) => {
                        e.stopPropagation();
                        clickBlock(block, e);
                    }}
                >
                    <RepeatImageShell
                        blockId={block.id}
                        resolvedBlockIcon={resolvedBlockIcon}
                        renderCountBadge={renderCountBadge}
                        isDraggingOver={isDraggingOver}
                        onDragOver={handleContainerDragOver}
                        onDragLeave={handleContainerDragLeave}
                        onDrop={handleContainerDrop}
                        contentWidthOverride={repeatContentWidth}
                    >
                        {nestedChildren}
                    </RepeatImageShell>
                </div>
            </div>
        );
    }

    return (
        <ScriptBlock
            block={block}
            index={index}
            parentContainerId={parentContainerId}
            resolveBlockPosition={resolveBlockPosition}
            draggedBlock={draggedBlock}
            dragPreviewActive={dragPreviewActive}
            dragPreviewIdSet={dragPreviewIdSet}
            dragPreviewReady={dragPreviewReady}
            handleBlockDragStart={handleBlockDragStart}
            handleBlockDragEnd={handleBlockDragEnd}
            handleBlockTouchStart={handleBlockTouchStart}
            handleBlockTouchMove={handleBlockTouchMove}
            handleBlockTouchEnd={handleBlockTouchEnd}
            clickBlock={clickBlock}
            handleBadgeClick={handleBadgeClick}
            handleBadgeDoubleClick={handleBadgeDoubleClick}
            renderInlineNumberInput={renderInlineNumberInput}
            renderInlineTextInput={renderInlineTextInput}
            globalCameraState={globalCameraState}
            isScriptRunning={isScriptRunning}
            handleRunClick={handleRunClick}
            dispatch={dispatch}
            histPayload={histPayload}
            editableTarget={editableTarget}
            inlineEdit={inlineEdit}
            openColorPicker={openColorPicker}
            handlePinBlockClick={handlePinBlockClick}
            languageOptions={languageOptions}
        />
    );
}

function ScriptBlock({
    block,
    index,
    parentContainerId = "main",
    resolveBlockPosition,
    draggedBlock,
    dragPreviewActive,
    dragPreviewIdSet,
    dragPreviewReady,
    handleBlockDragStart,
    handleBlockDragEnd,
    handleBlockTouchStart,
    handleBlockTouchMove,
    handleBlockTouchEnd,
    clickBlock,
    handleBadgeClick,
    handleBadgeDoubleClick,
    renderInlineNumberInput,
    renderInlineTextInput,
    globalCameraState,
    isScriptRunning,
    handleRunClick,
    dispatch,
    histPayload,
    editableTarget,
    inlineEdit,
    openColorPicker,
    handlePinBlockClick,
    languageOptions,
}) {
    const b = block;
    const i = index;
    const blockIconSrc = resolveBlockIcon(b);
    const hasBlockIcon = Boolean(blockIconSrc);
    const isTopLevel = parentContainerId === "main";
    const resolvedPosition = isTopLevel && typeof resolveBlockPosition === "function"
        ? resolveBlockPosition(b)
        : { x: 0, y: 0 };
    const blockX = resolvedPosition?.x || 0;
    const blockY = resolvedPosition?.y || 0;
    const blockStyle = isTopLevel
        ? { position: "absolute", left: `${blockX}px`, top: `${blockY}px`, zIndex: 10 }
        : undefined;
    const isDraggable = b.category !== "end";
    const draggingClass = isTopLevel && draggedBlock?.index === i ? "dragging" : "";
    const categoryClass = b.category === "robot" ? "robot-block" : "";
    const previewHiddenClass = isTopLevel && dragPreviewActive && dragPreviewIdSet?.has(b.id) && dragPreviewReady ? "drag-hidden" : "";
    const composedClass = [categoryClass, draggingClass, previewHiddenClass].filter(Boolean).join(" ");

    const runBlock = () => {
        if (!isScriptRunning) {
            handleRunClick?.();
        }
    };

    const shouldRunOnClick = (candidate) => {
        if (!candidate) return false;
        if (candidate.category === "motion") return true;
        if (candidate.category === "looks") return true;
        if (candidate.category === "control") return true;
        if (candidate.category === "stembot") return true;
        return false;
    };

    const sharedDragHandlers = isDraggable
        ? {
            draggable: true,
            onDragStart: (ev) => handleBlockDragStart?.(ev, b, i, parentContainerId),
            onDragEnd: (ev) => handleBlockDragEnd?.(ev, b, i, parentContainerId),
            onTouchStart: (ev) => handleBlockTouchStart?.(ev, b, i, parentContainerId),
            onTouchMove: (ev) => handleBlockTouchMove?.(ev, b, i, parentContainerId),
            onTouchEnd: (ev) => handleBlockTouchEnd?.(ev, b, i, parentContainerId),
            onTouchCancel: (ev) => handleBlockTouchEnd?.(ev, b, i, parentContainerId),
        }
        : {
            draggable: false,
        };

    if (b.type === "camera") {
        return (
            <div
                key={b.id}
                className={`block-palette-title ${composedClass}`}
                style={blockStyle}
                title="Camera Control - Click dropdown to turn on/off camera"
                {...sharedDragHandlers}
                onClick={(e) => clickBlock(b, e)}
            >
                <img className="block-bg" src={b.puzzleBg} alt="" draggable={false} />
                <select
                    className="camera-select-dropdown"
                    value={globalCameraState}
                    onChange={(e) => {
                        e.stopPropagation();
                        const newState = e.target.value;
                        dispatch(pushUndoState(histPayload));
                        dispatch(setCameraState(newState));
                        if (newState === "on") {
                            window.humanDetectionController?.startCamera();
                        } else {
                            window.humanDetectionController?.stopCamera();
                        }
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                    onTouchEnd={(e) => e.stopPropagation()}
                >
                    <option value="off">off</option>
                    <option value="on">on</option>
                </select>
                <img
                    src="./assets/ui/camera.svg"
                    style={{
                        position: "absolute",
                        top: "42%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: "35px",
                        height: "35px",
                        zIndex: 2,
                    }}
                    alt=""
                />
            </div>
        );
    }

    if (b.type === "video_transparency") {
        return (
            <div
                key={b.id}
                className={`block-palette-title ${composedClass}`}
                style={blockStyle}
                title="Set Video Transparency - Click to change opacity"
                {...sharedDragHandlers}
                onClick={(e) => clickBlock(b, e)}
            >
                <img className="block-bg" src={b.puzzleBg} alt="" draggable={false} />
                {hasBlockIcon && <img className="block-icon" src={blockIconSrc} alt={b.name} draggable={false} />}
                <span className="block-label">{b.opacity}%</span>
            </div>
        );
    }

    if (b.type === "ttsSetLanguage") {
        const options = Array.isArray(languageOptions) && languageOptions.length
            ? languageOptions
            : [{ code: "en-US", label: "English" }];
        const currentLang = b.selectedLang
            || b.lang
            || editableTarget?.speechSettings?.lang
            || options[0]?.code
            || "en-US";

        const handleLanguageChange = (nextLang) => {
            dispatch(pushUndoState(histPayload));
            const selected = options.find((opt) => opt.code === nextLang);
            const payload = {
                blockId: b.id,
                data: {
                    selectedLang: nextLang,
                    selectedLangLabel: selected?.label || nextLang,
                }
            };
            if (editableTarget?.type === "simulatorRobot") {
                payload.robotId = editableTarget.id;
            } else {
                payload.actorId = editableTarget?.id;
            }
            dispatch(updateBlockCustomData(payload));
        };

        return (
            <div
                key={b.id}
                className={`block-palette-title ${composedClass}`}
                style={blockStyle}
                title="Set language for speech and bubble"
                {...sharedDragHandlers}
                onClick={(e) => e.stopPropagation()}
            >
                <img className="block-bg" src={b.puzzleBg} alt="" draggable={false} />
                {hasBlockIcon && <img className="block-icon" src={blockIconSrc} alt={b.name} draggable={false} />}
                <select
                    className="camera-select-dropdown language-select-dropdown"
                    value={currentLang}
                    onChange={(e) => handleLanguageChange(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                    onTouchEnd={(e) => e.stopPropagation()}
                >
                    {options.map((opt) => (
                        <option key={opt.code} value={opt.code}>{opt.label || opt.code}</option>
                    ))}
                </select>
            </div>
        );
    }

    if (b.type === "aiSetPersona") {
        const personaOptions = [
            { value: "ai", label: "AI" },
            { value: "friendly", label: "Friendly" },
            { value: "sarcastic", label: "Sarcastic" },
        ];
        const currentPersona = typeof b.persona === "string" ? b.persona : "ai";

        const handlePersonaChange = (nextPersona) => {
            dispatch(pushUndoState(histPayload));
            const payload = {
                blockId: b.id,
                data: { persona: nextPersona },
            };
            if (editableTarget?.type === "simulatorRobot") {
                payload.robotId = editableTarget.id;
            } else {
                payload.actorId = editableTarget?.id;
            }
            dispatch(updateBlockCustomData(payload));
        };

        return (
            <div
                key={b.id}
                className={`block-palette-title ${composedClass}`}
                style={blockStyle}
                title="Set AI persona"
                {...sharedDragHandlers}
                onClick={(e) => e.stopPropagation()}
            >
                <img className="block-bg" src={b.puzzleBg} alt="" draggable={false} />
                {hasBlockIcon && <img className="block-icon" src={blockIconSrc} alt={b.name} draggable={false} />}
                <select
                    className="camera-select-dropdown"
                    value={currentPersona}
                    onChange={(e) => handlePersonaChange(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                    onTouchEnd={(e) => e.stopPropagation()}
                >
                    {personaOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
            </div>
        );
    }

    if (b.type === "aiHighlightPos") {
        const posOptions = [
            { value: "noun", label: "Noun" },
            { value: "verb", label: "Verb" },
            { value: "adjective", label: "Adjective" },
            { value: "adverb", label: "Adverb" },
        ];
        const currentPos = typeof b.posType === "string" ? b.posType : "noun";

        const handlePosChange = (nextPos) => {
            dispatch(pushUndoState(histPayload));
            const payload = {
                blockId: b.id,
                data: { posType: nextPos },
            };
            if (editableTarget?.type === "simulatorRobot") {
                payload.robotId = editableTarget.id;
            } else {
                payload.actorId = editableTarget?.id;
            }
            dispatch(updateBlockCustomData(payload));
        };

        return (
            <div
                key={b.id}
                className={`block-palette-title ${composedClass}`}
                style={blockStyle}
                title="Highlight selected parts of speech"
                {...sharedDragHandlers}
                onClick={(e) => e.stopPropagation()}
            >
                <img className="block-bg" src={b.puzzleBg} alt="" draggable={false} />
                {hasBlockIcon && <img className="block-icon" src={blockIconSrc} alt={b.name} draggable={false} />}
                <select
                    className="camera-select-dropdown"
                    value={currentPos}
                    onChange={(e) => handlePosChange(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                    onTouchEnd={(e) => e.stopPropagation()}
                >
                    {posOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
            </div>
        );
    }

    if (b.type === "facePartAction") {
        const normalizePart = (p = "nose") => {
            const map = {
                "left eye": "leftEye",
                "right eye": "rightEye",
                "between eyes": "betweenEyes",
                "left ear": "leftEar",
                "right ear": "rightEar",
                "top of head": "topOfHead",
            };
            if (typeof p !== "string") return "nose";
            return map[p] || p;
        };
        const faceOptions = [
            { value: "nose", label: "nose" },
            { value: "mouth", label: "mouth" },
            { value: "leftEye", label: "left eye" },
            { value: "rightEye", label: "right eye" },
            { value: "betweenEyes", label: "between eyes" },
            { value: "leftEar", label: "left ear" },
            { value: "rightEar", label: "right ear" },
            { value: "topOfHead", label: "top of head" },
        ];
        const currentPart = normalizePart(b.part || "nose");

        return (
            <div
                key={b.id}
                className={`block-palette-title ${composedClass}`}
                style={blockStyle}
                title="Go to selected face part"
                {...sharedDragHandlers}
                onClick={(e) => e.stopPropagation()}
            >
                <img className="block-bg" src={b.puzzleBg} alt="" draggable={false} />
                {hasBlockIcon && <img className="block-icon" src={blockIconSrc} alt={b.name} draggable={false} />}
                <select
                    className="camera-select-dropdown"
                    value={currentPart}
                    onChange={(e) => {
                        const newPart = e.target.value;
                        dispatch(pushUndoState(histPayload));
                        if (editableTarget.type === "simulatorRobot") {
                            dispatch(updateSimulatorBlockCount({
                                robotId: editableTarget.id,
                                blockId: b.id,
                                newCount: 0,
                                property: "part",
                                value: newPart,
                            }));
                        } else {
                            dispatch(updateBlockCount({
                                actorId: editableTarget.id,
                                blockId: b.id,
                                newCount: 0,
                                property: "part",
                                value: newPart,
                            }));
                        }
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                    onTouchEnd={(e) => e.stopPropagation()}
                >
                    {faceOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
            </div>
        );
    }

    if (b.type === "emotionDetected") {
        const emotionOptions = [
            { value: "happy", label: "Happy" },
            { value: "sad", label: "Sad" },
            { value: "surprised", label: "Surprised" },
            { value: "angry", label: "Angry" },
        ];
        const currentExpression = typeof b.expression === "string" ? b.expression : "happy";

        const handleExpressionChange = (newExpression) => {
            dispatch(pushUndoState(histPayload));
            const payload = {
                blockId: b.id,
                newCount: 0,
                property: "expression",
                value: newExpression,
            };
            if (editableTarget.type === "simulatorRobot") {
                dispatch(updateSimulatorBlockCount({
                    ...payload,
                    robotId: editableTarget.id,
                }));
            } else {
                dispatch(updateBlockCount({
                    ...payload,
                    actorId: editableTarget.id,
                }));
            }
        };

        return (
            <div
                key={b.id}
                className={`block-palette-title ${composedClass}`}
                style={blockStyle}
                title="Run the next blocks when the selected emotion is detected"
                {...sharedDragHandlers}
                onClick={(e) => e.stopPropagation()}
            >
                <img className="block-bg" src={b.puzzleBg} alt="" draggable={false} />
                {hasBlockIcon && (
                    <img className="block-icon" src={blockIconSrc} alt={b.name} draggable={false} />
                )}
                <select
                    className="camera-select-dropdown"
                    value={currentExpression}
                    onChange={(e) => handleExpressionChange(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                    onTouchEnd={(e) => e.stopPropagation()}
                >
                    {emotionOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
            </div>
        );
    }

    if (b.type === "handPartAction") {
        const currentFinger = normalizeFinger(b.finger || DEFAULT_FINGER);
        return (
            <div
                key={b.id}
                className={`block-palette-title ${composedClass}`}
                style={blockStyle}
                title="Go to selected finger tip"
                {...sharedDragHandlers}
                onClick={(e) => e.stopPropagation()}
            >
                <img className="block-bg" src={b.puzzleBg} alt="" draggable={false} />
                {hasBlockIcon && (
                    <img className="block-icon" src={blockIconSrc} alt={b.name} draggable={false} />
                )}
                <select
                    className="camera-select-dropdown"
                    value={currentFinger}
                    onChange={(e) => {
                        const nextFinger = e.target.value;
                        dispatch(pushUndoState(histPayload));
                        if (editableTarget?.type === "simulatorRobot") {
                            dispatch(updateSimulatorBlockCount({
                                robotId: editableTarget.id,
                                blockId: b.id,
                                newCount: 0,
                                property: "finger",
                                value: nextFinger,
                            }));
                        } else {
                            dispatch(updateBlockCount({
                                actorId: editableTarget.id,
                                blockId: b.id,
                                newCount: 0,
                                property: "finger",
                                value: nextFinger,
                            }));
                        }
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                    onTouchEnd={(e) => e.stopPropagation()}
                >
                    {HAND_FINGER_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
            </div>
        );
    }

    if (b.type === "setPathColor") {
        return (
            <div
                key={b.id}
                className={`block-palette-title ${composedClass}`}
                style={blockStyle}
                title="Set Path Color - Click to choose color"
                {...sharedDragHandlers}
                onClick={(e) => openColorPicker(b, e)}
            >
                <img className="block-bg" src={b.puzzleBg} alt="" draggable={false} />
                {hasBlockIcon && (
                    <img className="block-icon" src={blockIconSrc} alt={b.name} draggable={false} />
                )}
            </div>
        );
    }

    // 🎨 Robot LED Color Block - Same pattern as Set Path Color
    if (b.type === "robot_led_color" || b.type === "robot_led") {
        return (
            <div
                key={b.id}
                className={`block-palette-title ${composedClass}`}
                style={blockStyle}
                title="Set LED Color - Click to choose color"
                {...sharedDragHandlers}
                onClick={(e) => openColorPicker(b, e, "ledColor")}             >
                <img className="block-bg" src={b.puzzleBg} alt="" draggable={false} />
                {hasBlockIcon && (
                    <img className="block-icon" src={blockIconSrc} alt={b.name} draggable={false} />
                )}
            </div>
        );
    }

    // LED Ring Color Block
    if (b.type === "robot_led_ring_color" || b.type === "robot_led_ring_all") {
        const title = b.type === "robot_led_ring_all"
            ? "Set All Ring LEDs - Click to choose color"
            : "Set LED Ring Color - Click to choose color";
        return (
            <div key={b.id} className={`block-palette-title ${composedClass}`}
                style={blockStyle}
                title={title}
                {...sharedDragHandlers}
                onClick={(e) => openColorPicker(b, e, "ledRingColor")}
            >
                <img className="block-bg" src={b.puzzleBg} alt="" draggable={false} />
                {hasBlockIcon && <img className="block-icon" src={blockIconSrc} alt={b.name} draggable={false} />}
            </div>
        );
    }


    const renderMotionBadge = () => {
        if (b.category !== "motion" || b.name === "Go To Random Position") return null;
        const value = typeof b.count === "number" ? b.count : 1;
        if (inlineEdit?.blockId === b.id && inlineEdit?.mode === "number") {
            return renderInlineNumberInput(b);
        }
        return (
            <button
                type="button"
                className="block-count-badge"
                onClick={(ev) => handleBadgeClick(ev, b)}
                onDoubleClick={(ev) => handleBadgeDoubleClick(ev, b, "number", value)}
                onTouchEnd={(ev) => handleBadgeClick(ev, b)}
            >
                {value}
            </button>
        );
    };

    const renderSensorMoveBadge = () => {
        const isSensorMove = b.category === "sensors"
            && ["Move Forward", "Move Backward", "Move Up", "Move Down"].includes(b.name);
        if (!isSensorMove) return null;
        const value = typeof b.count === "number" ? b.count : 1;
        if (inlineEdit?.blockId === b.id && inlineEdit?.mode === "number") {
            return renderInlineNumberInput(b);
        }
        return (
            <button
                type="button"
                className="block-count-badge"
                onClick={(ev) => handleBadgeClick(ev, b)}
                onDoubleClick={(ev) => handleBadgeDoubleClick(ev, b, "number", value)}
                onTouchEnd={(ev) => handleBadgeClick(ev, b)}
            >
                {value}
            </button>
        );
    };

    const renderControlWaitBadge = () => {
        if (!(b.category === "control" && b.name === "Wait")) return null;
        const value = typeof b.count === "number" ? b.count : 10;
        if (inlineEdit?.blockId === b.id && inlineEdit?.mode === "number") {
            return renderInlineNumberInput(b);
        }
        return (
            <button
                type="button"
                className="block-count-badge"
                onClick={(ev) => handleBadgeClick(ev, b)}
                onDoubleClick={(ev) => handleBadgeDoubleClick(ev, b, "number", value)}
                onTouchEnd={(ev) => handleBadgeClick(ev, b)}
            >
                {value}
            </button>
        );
    };

    const renderSpeedDropdown = () => {
        const isControlSpeed = b.category === "control" && b.name === "Speed";
        const isServoSpeed = b.category === "robot" && (b.name === "Set Servo Speed" || b.type === "robot_servo_speed");
        if (!isControlSpeed && !isServoSpeed) return null;

        const currentValue = isControlSpeed
            ? (typeof b.speedMultiplier === "number" ? b.speedMultiplier : typeof b.count === "number" ? b.count : 1)
            : (typeof b.count === "number" ? b.count : 60);

        return (
            <select
                className={`block-speed-dropdown${isServoSpeed ? " block-speed-dropdown--servo" : ""}`}
                value={isControlSpeed ? currentValue.toFixed(1) : String(Math.round(currentValue))}
                title={isServoSpeed ? "deg/s" : undefined}
                onChange={(ev) => {
                    const value = isControlSpeed ? parseFloat(ev.target.value) : parseInt(ev.target.value, 10);
                    const blockId = b.id;
                    dispatch(pushUndoState(histPayload));
                    if (editableTarget.type === "simulatorRobot") {
                        dispatch(updateSimulatorBlockCount({
                            robotId: editableTarget.id,
                            blockId,
                            newCount: value,
                            property: isControlSpeed ? "speedMultiplier" : "count",
                            value: value,
                        }));
                    } else {
                        dispatch(updateBlockCount({
                            actorId: editableTarget.id,
                            blockId,
                            newCount: value,
                            property: isControlSpeed ? "speedMultiplier" : "count",
                            value: value,
                        }));
                    }
                }}
                onClick={(ev) => ev.stopPropagation()}
                onTouchStart={(ev) => ev.stopPropagation()}
                onTouchEnd={(ev) => ev.stopPropagation()}
            >
                {isControlSpeed
                    ? ["0.5", "1.0", "1.5", "2.0"].map((opt) => (
                        <option key={opt} value={opt}>{opt}x</option>
                    ))
                    : ["30", "60", "120", "180"].map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                    ))}
            </select>
        );
    };

    const renderSoundEffectDropdown = () => {
        if (!(b.category === "robot" && b.type === "robot_sound_effect")) return null;
        const currentValue = typeof b.soundType === "string" ? b.soundType : "happy";
        const options = [
            { value: "happy", label: "happy" },
            { value: "super_happy", label: "super happy+" },
            { value: "smile", label: "smile" },
            { value: "sad", label: "sad" },
            { value: "confused", label: "confused" },
            { value: "cuddly", label: "cuddly" },
            { value: "wink", label: "wink" },
            { value: "nope", label: "nope" },
            { value: "ohooh", label: "OhOoh" },
            { value: "astonished", label: "astonished" },
            { value: "sleep", label: "sleep" }
        ];
        return (
            <select
                className="block-sound-dropdown"
                value={currentValue}
                onChange={(ev) => {
                    const value = ev.target.value;
                    const blockId = b.id;
                    dispatch(pushUndoState(histPayload));
                    if (editableTarget.type === "simulatorRobot") {
                        dispatch(updateBlockCustomData({
                            robotId: editableTarget.id,
                            blockId,
                            data: { soundType: value }
                        }));
                    } else {
                        dispatch(updateBlockCustomData({
                            actorId: editableTarget.id,
                            blockId,
                            data: { soundType: value }
                        }));
                    }
                }}
                onClick={(ev) => ev.stopPropagation()}
                onTouchStart={(ev) => ev.stopPropagation()}
                onTouchEnd={(ev) => ev.stopPropagation()}
            >
                {options.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
            </select>
        );
    };

    const renderRobotBadge = () => {
        if (b.category !== "robot") return null;
        const skipTypes = new Set([
            "robot_stop",
            "robot_led_on",
            "robot_led_off",
            "robot_led_color",
            "robot_led_ring_on",
            "robot_led_ring_off",
            "robot_led_ring_color",
            "robot_led_ring_rainbow",
            "robot_led_ring_all",
            "robot_servo_speed",
            "robot_sound_effect",
            "robot_play_tone",
            "robot_buzzer",
            "robot_distance"
        ]);
        if (skipTypes.has(b.type)) return null;
        if (typeof b.count !== "number") return null;
        if (inlineEdit?.blockId === b.id && inlineEdit?.mode === "number") {
            return renderInlineNumberInput(b);
        }
        return (
            <button
                type="button"
                className="block-count-badge"
                onClick={(ev) => handleBadgeClick(ev, b)}
                onDoubleClick={(ev) => handleBadgeDoubleClick(ev, b, "number", b.count)}
                onTouchEnd={(ev) => handleBadgeClick(ev, b)}
            >
                {b.count}
            </button>
        );
    };

    const renderLooksBadge = () => {
        if (b.category === "looks" && (b.name === "Say" || b.type === "say")) {
            const label = (typeof b.message === "string" && b.message.trim().length)
                ? b.message.trim()
                : "hi";
            if (inlineEdit?.blockId === b.id && inlineEdit?.mode === "text") {
                return renderInlineTextInput();
            }
            return (
                <button
                    type="button"
                    className="block-count-badge"
                    style={{ textTransform: "none" }}
                    onClick={(ev) => handleBadgeClick(ev, b)}
                    onDoubleClick={(ev) => handleBadgeDoubleClick(ev, b, "text", label)}
                    onTouchEnd={(ev) => handleBadgeClick(ev, b)}
                >
                    {label.slice(0, 6)}
                </button>
            );
        }
        if (b.category === "looks" && b.type === "scriptText") {
            const raw = typeof b.scriptText === "string" ? b.scriptText : "";
            const firstLine = raw.split(/\r?\n/).find((line) => line.trim().length) || "script";
            return (
                <button
                    type="button"
                    className="block-count-badge"
                    style={{ textTransform: "none" }}
                    onClick={(ev) => handleBadgeClick(ev, b)}
                    onTouchEnd={(ev) => handleBadgeClick(ev, b)}
                >
                    {firstLine.slice(0, 6)}
                </button>
            );
        }
        if (b.category === "looks" && ["Grow Size", "Shrink Size"].includes(b.name)) {
            const value = typeof b.count === "number" ? b.count : 2;
            if (inlineEdit?.blockId === b.id && inlineEdit?.mode === "number") {
                return renderInlineNumberInput(b);
            }
            return (
                <button
                    type="button"
                    className="block-count-badge"
                    onClick={(ev) => handleBadgeClick(ev, b)}
                    onDoubleClick={(ev) => handleBadgeDoubleClick(ev, b, "number", value)}
                    onTouchEnd={(ev) => handleBadgeClick(ev, b)}
                >
                    {value}
                </button>
            );
        }
        return null;
    };

    const renderSoundBadge = () => {
        if (b.category === "sound" && b.type === "ttsSpeak") {
            const label = (typeof b.message === "string" && b.message.trim().length)
                ? b.message.trim()
                : "hi";
            if (inlineEdit?.blockId === b.id && inlineEdit?.mode === "text") {
                return renderInlineTextInput();
            }
            return (
                <button
                    type="button"
                    className="block-count-badge"
                    style={{ textTransform: "none" }}
                    onClick={(ev) => handleBadgeClick(ev, b)}
                    onDoubleClick={(ev) => handleBadgeDoubleClick(ev, b, "text", label)}
                    onTouchEnd={(ev) => handleBadgeClick(ev, b)}
                >
                    {label.slice(0, 6)}
                </button>
            );
        }
        return null;
    };

    const renderStembotBadge = () => {
        if (b.category === "stembot" && b.type === "aiPrompt") {
            const label = (typeof b.message === "string" && b.message.trim().length)
                ? b.message.trim()
                : "prompt";
            if (inlineEdit?.blockId === b.id && inlineEdit?.mode === "text") {
                return renderInlineTextInput();
            }
            return (
                <button
                    type="button"
                    className="block-count-badge"
                    style={{ textTransform: "none" }}
                    onClick={(ev) => handleBadgeClick(ev, b)}
                    onDoubleClick={(ev) => handleBadgeDoubleClick(ev, b, "text", label)}
                    onTouchEnd={(ev) => handleBadgeClick(ev, b)}
                >
                    {label.slice(0, 6)}
                </button>
            );
        }
        return null;
    };

    const renderStembotWordLimitBadge = () => {
        if (b.category !== "stembot" || b.type !== "aiWordLimit") return null;
        const value = typeof b.count === "number" ? b.count : 40;
        if (inlineEdit?.blockId === b.id && inlineEdit?.mode === "number") {
            return renderInlineNumberInput(b);
        }
        return (
            <button
                type="button"
                className="block-count-badge"
                onClick={(ev) => handleBadgeClick(ev, b)}
                onDoubleClick={(ev) => handleBadgeDoubleClick(ev, b, "number", value)}
                onTouchEnd={(ev) => handleBadgeClick(ev, b)}
            >
                {value}
            </button>
        );
    };

    const renderStembotCreativityBadge = () => {
        if (b.category !== "stembot" || b.type !== "aiCreativity") return null;
        const value = typeof b.count === "number" ? b.count : 0.7;
        const normalized = Number.isFinite(value) ? (value > 1 ? value / 100 : value) : 0.7;
        const displayValue = Math.max(0, Math.min(1, normalized));
        const displayLabel = displayValue.toFixed(2);
        if (inlineEdit?.blockId === b.id && inlineEdit?.mode === "number") {
            return renderInlineNumberInput(b);
        }
        return (
            <button
                type="button"
                className="block-count-badge"
                onClick={(ev) => handleBadgeClick(ev, b)}
                onDoubleClick={(ev) => handleBadgeDoubleClick(ev, b, "number", displayValue)}
                onTouchEnd={(ev) => handleBadgeClick(ev, b)}
            >
                {displayLabel}
            </button>
        );
    };

    const renderGoToPageBadge = () => {
        if (b.type !== "goToPage") return null;
        const value = typeof b.count === "number" ? b.count : 2;
        if (inlineEdit?.blockId === b.id && inlineEdit?.mode === "number") {
            return renderInlineNumberInput(b);
        }
        return (
            <button
                type="button"
                className="block-count-badge"
                onClick={(ev) => handleBadgeClick(ev, b)}
                onDoubleClick={(ev) => handleBadgeDoubleClick(ev, b, "number", value)}
                onTouchEnd={(ev) => handleBadgeClick(ev, b)}
            >
                Pg {value}
            </button>
        );
    };

    const renderMessageColorDropdown = () => {
        if (!isMessageBlock(b)) return null;
        const currentColor = normalizeMessageColor(b.messageColor || DEFAULT_MESSAGE_COLOR);

        const applyColor = (nextColor) => {
            dispatch(pushUndoState(histPayload));
            if (editableTarget.type === "simulatorRobot") {
                dispatch(updateSimulatorBlockColor({
                    robotId: editableTarget.id,
                    blockId: b.id,
                    color: nextColor,
                    property: "messageColor",
                }));
            } else {
                dispatch(updateBlockColor({
                    actorId: editableTarget.id,
                    blockId: b.id,
                    color: nextColor,
                    property: "messageColor",
                }));
            }
        };

        return (
            <select
                className="message-color-dropdown"
                value={currentColor}
                onChange={(ev) => {
                    ev.stopPropagation();
                    applyColor(ev.target.value);
                }}
                onClick={(ev) => ev.stopPropagation()}
                onTouchStart={(ev) => ev.stopPropagation()}
                onTouchEnd={(ev) => ev.stopPropagation()}
            >
                {MESSAGE_COLOR_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
            </select>
        );
    };

    return (
        <div
            key={b.id}
            className={`block-palette-title ${composedClass}`}
            style={blockStyle}
            data-block-id={b.id}
            title={
                b.name === "Wait"
                    ? `Wait ${b.count || 3} seconds - Click to change duration, drag out to remove`
                    : b.name === "Stop"
                        ? "Stop execution - Drag out to remove"
                        : b.name === "Speed"
                            ? "Increase speed by 1.5x - Drag out to remove"
                            : b.name === "Set Servo Speed"
                                ? "Set servo speed - Drag out to remove"
                                : "Click to set count, drag out to remove"
            }
            {...sharedDragHandlers}
            onClick={(e) => {
                if (["setDigitalPin", "setPWM", "setDAC", "readAnalogPin", "readDigitalPin"].includes(b.type)) {
                    handlePinBlockClick(b, e);
                } else if (shouldRunOnClick(b)) {
                    e.stopPropagation();
                    runBlock();
                } else if (
                    (b.category === "control" && b.name === "Speed")
                    || (b.category === "robot" && (b.name === "Set Servo Speed" || b.type === "robot_servo_speed"))
                    || (b.category === "robot" && b.type === "robot_sound_effect")
                ) {
                    e.stopPropagation();
                } else {
                    clickBlock(b, e);
                }
            }}
        >
            <img className="block-bg" src={b.puzzleBg} alt="" draggable={false} />
            {hasBlockIcon && (
                <img className="block-icon" src={blockIconSrc} alt={b.name} draggable={false} />
            )}
            {b.soundData && <div className="custom-sound-script-indicator"></div>}
            {renderMotionBadge()}
            {renderSensorMoveBadge()}
            {renderControlWaitBadge()}
            {renderRobotBadge()}
            {renderSpeedDropdown()}
            {renderSoundEffectDropdown()}
            {renderLooksBadge()}
            {renderSoundBadge()}
            {renderStembotBadge()}
            {renderStembotWordLimitBadge()}
            {renderStembotCreativityBadge()}
            {renderGoToPageBadge()}
            {renderMessageColorDropdown()}
        </div>
    );
}


// ========== END BLOCK RENDERER ==========

// ========== HELPER: Calculate Block Chain ==========
/**
 * When dragging a block, get all blocks connected to the right via nextBlockId
 */
function calculateBlockChain(scripts, draggedBlock) {
    if (!scripts || !draggedBlock) {
        return { length: 0, blocks: [] };
    }

    const chainBlocks = [draggedBlock];

    if (draggedBlock?.category === 'end') {
        return { length: 1, blocks: chainBlocks };
    }

    let currentBlock = draggedBlock;
    const visited = new Set([draggedBlock.id]);

    while (currentBlock?.nextBlockId) {
        const nextBlock = scripts.find(b => b.id === currentBlock.nextBlockId);
        if (!nextBlock || visited.has(nextBlock.id)) break;

        chainBlocks.push(nextBlock);
        visited.add(nextBlock.id);
        currentBlock = nextBlock;
    }

    return { length: chainBlocks.length, blocks: chainBlocks };
}

function getDragChain(scripts, draggedBlock) {
    if (!draggedBlock) {
        return { length: 0, blocks: [] };
    }
    return calculateBlockChain(scripts, draggedBlock);
}

const BLOCK_RENDER_WIDTH = 76;
const START_BLOCK_RENDER_WIDTH = 84;
const BLOCK_OVERLAP = 11;
const BLOCK_WIDTH = BLOCK_RENDER_WIDTH - BLOCK_OVERLAP; // Snap width for standard blocks
const START_BLOCK_WIDTH = START_BLOCK_RENDER_WIDTH - BLOCK_OVERLAP; // Snap width for start blocks
const REPEAT_LEFT_SNAP_INSET = BLOCK_OVERLAP;
const REPEAT_RIGHT_SNAP_INSET = 0;
const NORMAL_CONNECTOR_Y = 0;
const REPEAT_RIGHT_CONNECTOR_Y = 16;
const REPEAT_LEFT_CONNECTOR_Y = REPEAT_RIGHT_CONNECTOR_Y;
const SNAP_HORIZONTAL_RANGE = 140;
const SNAP_VERTICAL_TOLERANCE = 35;
// 🔥 NEW: Detect if a block should snap to another block
function findSnapTarget(
    scripts,
    droppedBlock,
    dropX,
    dropY,
    ignoredIds = [],
    positionLookup = null,
    options = {}
) {
    const SNAP_DISTANCE = 80; // Pixels - how close to trigger snap
    const ignoredSet = new Set(ignoredIds || []);
    const chainHeadIsStart = options?.chainHeadIsStart ?? isFixedStartBlock(droppedBlock);
    const chainTailIsEnd = options?.chainTailIsEnd ?? false;

    let bestCandidate = null;
    let bestDistance = Infinity;

    const considerCandidate = (candidate) => {
        if (!candidate) return;
        if (candidate.distance < bestDistance) {
            bestDistance = candidate.distance;
            bestCandidate = candidate;
        }
    };

    for (const targetBlock of scripts) {
        if (!targetBlock) continue;
        if (ignoredSet.has(targetBlock.id)) continue;
        if (droppedBlock && targetBlock.id === droppedBlock.id) continue;

        const storedPosition = positionLookup?.get(targetBlock.id);
        const targetX = Number.isFinite(storedPosition?.x)
            ? storedPosition.x
            : Number.isFinite(targetBlock.x)
                ? targetBlock.x
                : null;
        const targetY = Number.isFinite(storedPosition?.y)
            ? storedPosition.y
            : Number.isFinite(targetBlock.y)
                ? targetBlock.y
                : null;

        if (targetX === null || targetY === null) continue;

        const targetHasLeftNeighbor = scripts.some(b => b && b.nextBlockId === targetBlock.id);
        const targetHasRightNeighbor = !!targetBlock.nextBlockId;

        const targetRight = getBlockRightSnapOffset(targetBlock);
        const droppedLeft = getBlockLeftSnapInset(droppedBlock);
        const targetRightY = getBlockRightConnectorOffsetY(targetBlock);
        const droppedLeftY = getBlockLeftConnectorOffsetY(droppedBlock);

        // Check if dropped to the RIGHT of target block
        const rightX = targetX + targetRight - droppedLeft;
        const rightY = targetY + targetRightY - droppedLeftY;

        const distanceToRight = Math.hypot(dropX - rightX, dropY - rightY);

        if (distanceToRight < SNAP_DISTANCE) {
            const rightAllowed =
                !chainHeadIsStart &&
                !isEndBlock(targetBlock) &&
                !(chainTailIsEnd && targetHasRightNeighbor);

            if (rightAllowed) {
                considerCandidate({
                    targetBlock,
                    targetBlockId: targetBlock.id,
                    snapPosition: 'right',
                    snapX: rightX,
                    snapY: rightY,
                    distance: distanceToRight
                });
            }
        }

        // Check if dropped to the LEFT of target block (if target has no left neighbor)
        const targetLeft = getBlockLeftSnapInset(targetBlock);
        const droppedRight = getBlockRightSnapOffset(droppedBlock);
        const targetLeftY = getBlockLeftConnectorOffsetY(targetBlock);
        const droppedRightY = getBlockRightConnectorOffsetY(droppedBlock);
        const leftX = targetX + targetLeft - droppedRight;
        const leftY = targetY + targetLeftY - droppedRightY;

        const distanceToLeft = Math.hypot(dropX - leftX, dropY - leftY);

        if (distanceToLeft < SNAP_DISTANCE && !targetHasLeftNeighbor) {
            const leftAllowed =
                !isFixedStartBlock(targetBlock) &&
                !chainTailIsEnd;

            if (leftAllowed) {
                considerCandidate({
                    targetBlock,
                    targetBlockId: targetBlock.id,
                    snapPosition: 'left',
                    snapX: leftX,
                    snapY: leftY,
                    distance: distanceToLeft
                });
            }
        }
    }

    return bestCandidate; // No snap target found => null
}

// 🔥 HELPER: Calculate visual X position based on chain
function calculateBlockX(scripts, block) {
    // Find if this block is connected to the right of another block
    const leftNeighbor = scripts.find(b => b.nextBlockId === block.id);

    if (leftNeighbor) {
        console.log(`📍 Block ${block.name} has left neighbor ${leftNeighbor.name}`);
        // This block should be positioned to the right of its left neighbor
        const leftX = calculateBlockX(scripts, leftNeighbor);
        return leftX + getBlockRightSnapOffset(leftNeighbor) - getBlockLeftSnapInset(block);
    }

    // This block is the start of a chain (or standalone)
    console.log(`📍 Block ${block.name} is chain start at x=${block.x}`);
    return block.x || 50;
}

// 🔥 HELPER: Calculate visual Y position based on chain
function calculateBlockY(scripts, block) {
    // Find if this block is connected to the right of another block
    const leftNeighbor = scripts.find(b => b.nextBlockId === block.id);

    if (leftNeighbor) {
        // This block should be at the same Y as its left neighbor
        const leftY = calculateBlockY(scripts, leftNeighbor);
        return leftY + getBlockRightConnectorOffsetY(leftNeighbor) - getBlockLeftConnectorOffsetY(block);
    }

    // This block is the start of a chain (or standalone)
    return block.y || 50;
}

function getBlockWidthForSnap(block) {
    if (!block) return BLOCK_WIDTH;
    return getBlockRightSnapOffset(block);
}

function snapUpdatesHorizontally(
    updates = [],
    scripts = [],
    chainBlockIds = [],
    dropX = 0,
    dropY = 0,
    clampX = (value) => value,
    clampY = (value) => value,
    positionLookup = null
) {
    if (!updates.length) return updates;

    const chainIdSet = new Set((chainBlockIds || []).filter(Boolean));
    const otherBlocks = scripts
        .map(block => {
            if (!block || chainIdSet.has(block.id)) {
                return null;
            }
            const storedPosition = positionLookup?.get(block.id);
            const targetX = Number.isFinite(storedPosition?.x)
                ? storedPosition.x
                : Number.isFinite(block.x)
                    ? block.x
                    : null;
            const targetY = Number.isFinite(storedPosition?.y)
                ? storedPosition.y
                : Number.isFinite(block.y)
                    ? block.y
                    : null;
            if (targetX === null || targetY === null) {
                return null;
            }
            return { ...block, x: targetX, y: targetY };
        })
        .filter(Boolean);
    if (!otherBlocks.length) return updates;

    const blockLookup = new Map(scripts.map(block => [block?.id, block]).filter(([id]) => Boolean(id)));
    const metaUpdates = updates.map(upd => ({
        ...upd,
        block: upd.blockOverride || blockLookup.get(upd.blockId) || null,
    }));
    const leftMost = metaUpdates.reduce(
        (left, upd) => ((upd.x ?? 0) < (left.x ?? 0) ? upd : left),
        metaUpdates[0]
    );
    const leftIsStart = isFixedStartBlock(leftMost.block);

    let bestDelta = null;
    let bestScore = Infinity;

    otherBlocks.forEach(target => {
        const targetX = target.x ?? 0;
        const targetY = target.y ?? 0;

        if (Math.abs(dropY - targetY) > SNAP_VERTICAL_TOLERANCE) {
            return;
        }

        let desiredX;
        let desiredY = targetY + getBlockRightConnectorOffsetY(target) - getBlockLeftConnectorOffsetY(leftMost.block);

        desiredX = targetX + getBlockRightSnapOffset(target) - getBlockLeftSnapInset(leftMost.block);

        const deltaX = desiredX - (leftMost.x ?? 0);
        const deltaY = desiredY - (leftMost.y ?? 0);
        const score = Math.abs(dropX - desiredX) + Math.abs(dropY - desiredY);

        if (score < bestScore && score <= SNAP_HORIZONTAL_RANGE) {
            bestScore = score;
            bestDelta = { deltaX, deltaY };
        }
    });

    if (!bestDelta) return updates;

    return metaUpdates.map(upd => ({
        blockId: upd.blockId,
        x: clampX((upd.x ?? 0) + bestDelta.deltaX),
        y: clampY((upd.y ?? 0) + bestDelta.deltaY),
    }));
}

function computeBlockPositions(
    scripts = [],
    maxWidth = DEFAULT_SCRIPTAREA_WIDTH,
    viewportHeight = DEFAULT_SCRIPTAREA_HEIGHT
) {
    const positions = new Map();
    if (!Array.isArray(scripts) || scripts.length === 0) {
        return positions;
    }

    const effectiveWidth = Math.max(300, Math.floor(maxWidth - 20));
    const effectiveHeight = Math.max(ROW_HEIGHT, Math.floor(viewportHeight || ROW_HEIGHT));

    const blockLookup = new Map();
    const incomingIds = new Set();

    scripts.forEach(block => {
        if (!block) return;
        blockLookup.set(block.id, block);
        if (block.nextBlockId) {
            incomingIds.add(block.nextBlockId);
        }
    });

    const manualPositionMap = new Map();
    scripts.forEach(block => {
        if (!block || incomingIds.has(block.id)) return;
        const manual = resolveRelativeBlockPosition(block, maxWidth, viewportHeight);
        if (manual) {
            manualPositionMap.set(block.id, manual);
        }
    });

    const heads = scripts.filter(block => block && !incomingIds.has(block.id));

    heads.forEach(head => {
        if (!head) return;
        let current = head;
        const headWidth = getBlockWidthForSnap(head);
        const manualHeadPos = manualPositionMap.get(head.id);
        const baseStartX = Number.isFinite(manualHeadPos?.x)
            ? manualHeadPos.x
            : Number.isFinite(head.x)
                ? head.x
                : 50;
        const clampedStartX = Math.min(
            Math.max(MIN_START_X, baseStartX),
            Math.max(MIN_START_X, effectiveWidth - headWidth)
        );
        let currentX = clampedStartX;
        let currentY = Number.isFinite(manualHeadPos?.y)
            ? manualHeadPos.y
            : Number.isFinite(head.y)
                ? head.y
                : 50;
        let lastY = currentY;
        const rowStartX = Number.isFinite(manualHeadPos?.x) ? manualHeadPos.x : clampedStartX;

        while (current && !positions.has(current.id)) {
            const currentWidth = getBlockWidthForSnap(current);
            const manual = manualPositionMap.get(current.id);
            const placedX = Number.isFinite(manual?.x)
                ? manual.x
                : Math.min(
                    Math.max(MIN_START_X, Number.isFinite(currentX) ? currentX : rowStartX),
                    effectiveWidth - currentWidth
                );
            const placedY = Number.isFinite(manual?.y)
                ? manual.y
                : Number.isFinite(lastY)
                    ? lastY
                    : 50;
            positions.set(current.id, { x: placedX, y: placedY });

            if (!current.nextBlockId) break;

            const next = blockLookup.get(current.nextBlockId);
            if (!next) break;

            const nextWidth = getBlockWidthForSnap(next);
            let nextX = placedX + getBlockRightSnapOffset(current) - getBlockLeftSnapInset(next);
            let nextY = placedY + getBlockRightConnectorOffsetY(current) - getBlockLeftConnectorOffsetY(next);

            if (nextX + nextWidth > effectiveWidth) {
                nextX = rowStartX;
                nextY = placedY + ROW_HEIGHT;
                if (nextY > effectiveHeight) {
                    nextY = effectiveHeight - ROW_HEIGHT;
                }
            }

            currentX = nextX;
            lastY = nextY;

            current = next;
        }
    });

    scripts.forEach(block => {
        if (!block || positions.has(block.id)) return;
        const fallbackX = Number.isFinite(block.x) ? block.x : 50;
        const manual = manualPositionMap.get(block.id);
        const fallbackY = Number.isFinite(manual?.y)
            ? manual.y
            : Number.isFinite(block.y)
                ? block.y
                : 50;
        positions.set(block.id, {
            x: Number.isFinite(manual?.x) ? manual.x : fallbackX,
            y: fallbackY
        });
    });

    return positions;
}

function alignUpdatesToSnap(updates = [], headId, snapX, snapY, clampX, clampY) {
    if (!Array.isArray(updates) || !updates.length || !headId) {
        return updates;
    }
    const headUpdate = updates.find(upd => upd.blockId === headId);
    if (!headUpdate) return updates;

    const baseX = Number.isFinite(headUpdate.x) ? headUpdate.x : 0;
    const baseY = Number.isFinite(headUpdate.y) ? headUpdate.y : 0;
    const offsetX = snapX - baseX;
    const offsetY = snapY - baseY;

    return updates.map(upd => ({
        blockId: upd.blockId,
        x: clampX((upd.x ?? 0) + offsetX),
        y: clampY((upd.y ?? 0) + offsetY),
    }));
}
export default function ScriptArea({ selectedActorId }) {
    const dispatch = useDispatch();
    const store = useStore();
    const { scenes, currentSceneIndex, simulatorRobots, selectedSimRobotId } = useSelector((s) => s.scene);
    const globalCameraState = useSelector((s) => s.scene.globalCameraState);

    const scene = scenes[currentSceneIndex];
    const stageActor = scene?.actors.find((a) => a.id === selectedActorId);

    // If a simulator robot is selected for editing, we will edit that robot's scripts.
    const selectedSimRobot = (simulatorRobots || []).find(r => r.id === selectedSimRobotId) || null;

    // editableTarget is either a stage actor (if no sim robot selected) or the selected simulator robot
    const editableTarget = selectedSimRobot || stageActor;

    const [pickerOpen, setPickerOpen] = useState(false);
    const [pickerPos, setPickerPos] = useState({ top: 0, left: 0 }); // new: position for picker
    const [tapBlock, setTapBlock] = useState(null);
    const [draggedBlock, setDraggedBlock] = useState(null); // retained for potential future use
    const [dragStarted, setDragStarted] = useState(false);  // retained for potential future use
    const [dragPreview, setDragPreview] = useState({
        active: false,
        ready: false,
        chainBlockIds: [],
        anchorBlockId: null,
        startClientX: 0,
        startClientY: 0,
        deltaX: 0,
        deltaY: 0,
    });
    const dragPreviewFrameRef = useRef({ rafId: null, pending: null });
    const [reorderDragging, setReorderDragging] = useState(false);
    const [isScriptRunning, setIsScriptRunning] = useState(false);
    const [colorPickerOpen, setColorPickerOpen] = useState(false);
    const [colorPickerPos, setColorPickerPos] = useState({ top: 0, left: 0 });
    const [colorPickerBlock, setColorPickerBlock] = useState(null);
    const [colorPickerValue, setColorPickerValue] = useState("#000000");
    const [colorPickerProperty, setColorPickerProperty] = useState("pathColor");
    const [languageOptions, setLanguageOptions] = useState(() => getAvailableTTSLanguages());
    const languageDropdownOptions = useMemo(() => {
        const displayNames = typeof Intl !== "undefined" && typeof Intl.DisplayNames === "function"
            ? new Intl.DisplayNames(["en"], { type: "language" })
            : null;
        return (languageOptions || []).map((option) => {
            const code = option.code || option.value || "en-US";
            const baseCode = code.split("-")[0];
            const displayLabel = displayNames ? displayNames.of(baseCode) : null;
            return {
                ...option,
                code,
                label: displayLabel || option.label || code
            };
        });
    }, [languageOptions]);
    const dragPreviewIdSet = useMemo(
        () => new Set((dragPreview.chainBlockIds || []).filter(Boolean)),
        [dragPreview.chainBlockIds]
    );
    const [languagePicker, setLanguagePicker] = useState({
        open: false,
        blockId: null,
        position: { top: 0, left: 0 },
        selected: getAvailableTTSLanguages()[0]?.code || "en-US",
    });
    const scriptAreaRef = useRef(null);
    const [scriptAreaSize, setScriptAreaSize] = useState({ width: DEFAULT_SCRIPTAREA_WIDTH, height: DEFAULT_SCRIPTAREA_HEIGHT });
    const [inlineEdit, setInlineEdit] = useState(null);
    const inlineInputRef = useRef(null);
    const badgeClickTimeoutRef = useRef(null);

    // --- PIN CONFIG MODAL STATE ---
    const [showPinModal, setShowPinModal] = useState(false);
    const [selectedPinBlock, setSelectedPinBlock] = useState(null);
    const [textModal, setTextModal] = useState({
        open: false,
        blockId: null,
        value: "",
        title: "Enter message"
    });
    const [scriptTextModal, setScriptTextModal] = useState({
        open: false,
        blockId: null,
        value: ""
    });

    const handlePinBlockClick = (block, e) => {
        if (!block) return;
        if (["setDigitalPin", "setPWM", "setDAC", "readAnalogPin", "readDigitalPin", "setPinMode"].includes(block.type)) {
            let type = "generic"; // default fallback

            if (block.type === "setDigitalPin") {
                type = "setDigital";
            } else if (block.type === "readDigitalPin") {
                type = "readDigital";
            }

            const blockData = {
                ...block,
                type, // 👈 pass the specific modal type
                pin: block.pin || "IO2",
                mode: block.mode || "input",
                state: block.state || "low",
            };

            setSelectedPinBlock(blockData);

            // compute picker/modal position if event available (useful if you want to show contextual UI)
            if (e?.currentTarget?.getBoundingClientRect) {
                const rect = e.currentTarget.getBoundingClientRect();
                setPickerPos({
                    top: rect.bottom + window.scrollY + 8,
                    left: rect.left + window.scrollX
                });
            }

            setShowPinModal(true);
        }
    };

    useEffect(() => {
        let mounted = true;
        const waitForVoices = async () => {
            try {
                await textToSpeechManager?.waitForVoices?.();
                if (mounted) {
                    setLanguageOptions(getAvailableTTSLanguages());
                }
            } catch (err) {
                console.warn("Failed to load voices for language picker:", err);
            }
        };
        waitForVoices();
        return () => {
            mounted = false;
        };
    }, []);

    useEffect(() => {
        setLanguagePicker((prev) => ({ ...prev, open: false, blockId: null }));
    }, [editableTarget?.id]);

    useLayoutEffect(() => {
        const updateBounds = () => {
            if (!scriptAreaRef.current) return;
            const rect = scriptAreaRef.current.getBoundingClientRect();
            setScriptAreaSize(prev => {
                const nextWidth = Math.round(rect.width);
                const nextHeight = Math.round(rect.height);
                if (prev.width === nextWidth && prev.height === nextHeight) {
                    return prev;
                }
                return { width: nextWidth, height: nextHeight };
            });
        };

        updateBounds();
        window.addEventListener('resize', updateBounds);
        return () => window.removeEventListener('resize', updateBounds);
    }, []);

    // --- end pin config state & handler ---

    // history target payload for all snapshots
    const histPayload =
        editableTarget?.type === "simulatorRobot"
            ? { robotId: editableTarget.id }
            : { actorId: editableTarget?.id };

    const getLatestScriptsForTarget = () => {
        const currentState = store.getState();
        if (histPayload?.robotId) {
            return currentState.scene?.simulatorRobots
                ?.find(r => r.id === histPayload.robotId)
                ?.scripts || [];
        }
        if (histPayload?.actorId) {
            const currentScene = currentState.scene?.scenes?.[currentState.scene?.currentSceneIndex];
            return currentScene?.actors
                ?.find(actor => actor.id === histPayload.actorId)
                ?.scripts || [];
        }
        return [];
    };

    useEffect(() => {
        if (inlineEdit && inlineInputRef.current) {
            inlineInputRef.current.focus();
            const node = inlineInputRef.current;
            const end = node.value?.length ?? 0;
            if (typeof node.setSelectionRange === 'function') {
                try {
                    node.setSelectionRange(end, end);
                } catch (err) {
                    // ignore in case of unsupported input types
                }
            }
        }
    }, [inlineEdit]);

    useEffect(() => {
        return () => {
            if (badgeClickTimeoutRef.current) {
                clearTimeout(badgeClickTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        setInlineEdit(null);
    }, [editableTarget?.id]);

    const openColorPicker = (block, e, property = "pathColor") => {
        if (!block) return;
        let rect = null;
        try {
            rect = e?.currentTarget?.getBoundingClientRect();
        } catch (err) {
            rect = null;
        }
        const top = rect ? rect.bottom + window.scrollY + 8 : window.innerHeight / 2;
        const left = rect ? rect.left + window.scrollX : window.innerWidth / 2;
        setColorPickerPos({ top, left });
        setColorPickerBlock(block);
        setColorPickerProperty(property);
        if (property === "targetColor") {
            setColorPickerValue(block.targetColor || "#FF0000");
        } else if (property === "ledColor") {
            setColorPickerValue(block.ledColor || "#FF0000");  // ← ADD THIS!
        } else {
            setColorPickerValue(block.pathColor || "#000000");
        }
        setColorPickerOpen(true);
    };
    const applyPathColor = (newColor) => {
        if (!editableTarget || !colorPickerBlock) return;
        const normalized = (newColor || "").toUpperCase();
        if (!/^#([0-9A-F]{3}){1,2}$/i.test(normalized)) {
            setColorPickerValue(newColor);
            return;
        }

        dispatch(pushUndoState(histPayload));
        const property = colorPickerProperty || "pathColor";
        console.log("🎨 Applying color:", normalized, "to property:", property);  // ← ADD THIS!

        if (editableTarget.type === "simulatorRobot") {
            dispatch(updateSimulatorBlockColor({
                robotId: editableTarget.id,
                blockId: colorPickerBlock.id,
                color: normalized,
                property
            }));
        } else {
            dispatch(updateBlockColor({
                actorId: editableTarget.id,
                blockId: colorPickerBlock.id,
                color: normalized,
                property
            }));
        }

        setColorPickerValue(normalized);
    };

    const closeColorPicker = () => {
        setColorPickerOpen(false);
        setColorPickerBlock(null);
        setColorPickerProperty("pathColor");
    };

    const getBlockById = (blockId) => {
        if (!editableTarget?.scripts) return null;
        return editableTarget.scripts.find((block) => block.id === blockId) || null;
    };

    const clampValueForBlock = (block, rawValue) => {
        if (!block) return Math.max(1, Number.isFinite(rawValue) ? rawValue : 1);
        if (block.name === "Set Video Transparency" || block.type === "video_transparency") {
            const numeric = Number.isFinite(rawValue) ? rawValue : 0;
            return Math.max(0, Math.min(100, numeric));
        }
        if (block.name === "Set Creativity" || block.type === "aiCreativity") {
            const numeric = Number.isFinite(rawValue) ? rawValue : 0;
            return Math.max(0, Math.min(1, numeric));
        }
        const numeric = Number.isFinite(rawValue) ? rawValue : 1;
        return Math.max(1, numeric);
    };

    const handleBadgeClick = (event, block) => {
        if (!block) return;
        event.stopPropagation();
        if (event.type === 'touchend') {
            clickBlock(block, event);
            return;
        }
        if (event.persist) event.persist();
        if (badgeClickTimeoutRef.current) {
            clearTimeout(badgeClickTimeoutRef.current);
        }
        badgeClickTimeoutRef.current = setTimeout(() => {
            clickBlock(block, event);
            badgeClickTimeoutRef.current = null;
        }, 180);
    };

    const beginInlineEdit = (block, mode, initialValue) => {
        if (!block) return;
        if (mode === 'number') {
            const allowDecimal = block?.type === "aiCreativity" || block?.name === "Set Creativity";
            const parsed = allowDecimal ? parseFloat(initialValue) : parseInt(initialValue, 10);
            const safeValue = clampValueForBlock(block, parsed);
            setInlineEdit({
                blockId: block.id,
                mode: 'number',
                value: String(safeValue || 1),
                allowDecimal,
            });
        } else {
            setInlineEdit({
                blockId: block.id,
                mode: 'text',
                value: initialValue || '',
            });
        }
    };

    const handleBadgeDoubleClick = (event, block, mode, initialValue) => {
        event.stopPropagation();
        if (badgeClickTimeoutRef.current) {
            clearTimeout(badgeClickTimeoutRef.current);
            badgeClickTimeoutRef.current = null;
        }
        beginInlineEdit(block, mode, initialValue);
    };

    const handleInlineInputChange = (value) => {
        setInlineEdit((prev) => {
            if (!prev) return prev;
            let nextValue = prev.mode === 'number'
                ? (prev.allowDecimal
                    ? value.replace(/[^\d.]/g, '')
                    : value.replace(/[^\d]/g, ''))
                : value;
            if (prev.mode === 'number' && prev.allowDecimal) {
                const dotIndex = nextValue.indexOf('.');
                if (dotIndex !== -1) {
                    nextValue = nextValue.slice(0, dotIndex + 1) + nextValue.slice(dotIndex + 1).replace(/\./g, '');
                }
            }
            return { ...prev, value: nextValue };
        });
    };

    const cancelInlineEdit = () => setInlineEdit(null);

    const commitInlineEdit = () => {
        if (!inlineEdit || !editableTarget) {
            setInlineEdit(null);
            return;
        }
        const targetBlock = getBlockById(inlineEdit.blockId);
        if (!targetBlock) {
            setInlineEdit(null);
            return;
        }
        if (inlineEdit.mode === 'number') {
            const parsed = inlineEdit.allowDecimal
                ? parseFloat(inlineEdit.value)
                : parseInt(inlineEdit.value, 10);
            const fallback = inlineEdit.allowDecimal ? 0 : 1;
            const safeValue = clampValueForBlock(targetBlock, Number.isNaN(parsed) ? fallback : parsed);
            setCount(safeValue, targetBlock);
        } else if (inlineEdit.mode === 'text') {
            const nextMessage = (inlineEdit.value || '').trim() || 'Hi';
            dispatch(pushUndoState(histPayload));
            if (editableTarget.type === 'simulatorRobot') {
                dispatch(updateSimulatorBlockMessage({
                    robotId: editableTarget.id,
                    blockId: targetBlock.id,
                    message: nextMessage
                }));
            } else {
                dispatch(updateBlockMessage({
                    actorId: editableTarget.id,
                    blockId: targetBlock.id,
                    message: nextMessage
                }));
            }
        }
        setInlineEdit(null);
    };

    const handleInlineInputKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            commitInlineEdit();
        } else if (event.key === 'Escape') {
            event.preventDefault();
            cancelInlineEdit();
        }
    };

    const computeInlineWidth = (value) => {
        const length = Math.max(1, value?.length || 1);
        return `${Math.max(2, length + 1)}ch`;
    };

    const renderInlineNumberInput = (block) => (
        <input
            ref={inlineInputRef}
            type="number"
            min={(block?.name === "Set Video Transparency" || block?.name === "Set Creativity" || block?.type === "aiCreativity") ? 0 : 1}
            step={(block?.name === "Set Creativity" || block?.type === "aiCreativity") ? 0.01 : 1}
            className="block-count-badge block-count-input"
            value={inlineEdit?.value ?? ''}
            style={{ width: computeInlineWidth(inlineEdit?.value) }}
            onChange={(e) => handleInlineInputChange(e.target.value)}
            onBlur={commitInlineEdit}
            onKeyDown={handleInlineInputKeyDown}
            inputMode="numeric"
        />
    );

    const renderInlineTextInput = () => (
        <input
            ref={inlineInputRef}
            type="text"
            className="block-count-badge block-count-input block-count-input--text"
            value={inlineEdit?.value ?? ''}
            style={{ width: computeInlineWidth(inlineEdit?.value) }}
            onChange={(e) => handleInlineInputChange(e.target.value)}
            onBlur={commitInlineEdit}
            onKeyDown={handleInlineInputKeyDown}
            maxLength={30}
        />
    );

    const clearDragPreview = useCallback(() => {
        const frameState = dragPreviewFrameRef.current;
        if (frameState?.rafId) {
            cancelAnimationFrame(frameState.rafId);
        }
        dragPreviewFrameRef.current = { rafId: null, pending: null };
        setDragPreview((prev) => {
            if (!prev.active) return prev;
            return {
                active: false,
                ready: false,
                chainBlockIds: [],
                anchorBlockId: null,
                startClientX: 0,
                startClientY: 0,
                deltaX: 0,
                deltaY: 0,
            };
        });
    }, []);

    const startDragPreview = useCallback((chainBlockIds, anchorBlockId, clientX, clientY) => {
        setDragPreview({
            active: true,
            ready: false,
            chainBlockIds: Array.isArray(chainBlockIds) ? chainBlockIds.filter(Boolean) : [],
            anchorBlockId: anchorBlockId || null,
            startClientX: Number.isFinite(clientX) ? clientX : 0,
            startClientY: Number.isFinite(clientY) ? clientY : 0,
            deltaX: 0,
            deltaY: 0,
        });
    }, []);

    const scheduleDragPreviewUpdate = useCallback((clientX, clientY) => {
        if (!dragPreviewFrameRef.current) return;
        dragPreviewFrameRef.current.pending = {
            clientX: Number.isFinite(clientX) ? clientX : 0,
            clientY: Number.isFinite(clientY) ? clientY : 0,
        };
        if (dragPreviewFrameRef.current.rafId) return;
        dragPreviewFrameRef.current.rafId = requestAnimationFrame(() => {
            dragPreviewFrameRef.current.rafId = null;
            const pending = dragPreviewFrameRef.current.pending;
            if (!pending) return;
            setDragPreview((prev) => {
                if (!prev.active) return prev;
                const deltaX = pending.clientX - prev.startClientX;
                const deltaY = pending.clientY - prev.startClientY;
                if (deltaX === prev.deltaX && deltaY === prev.deltaY && prev.ready) return prev;
                return { ...prev, deltaX, deltaY, ready: true };
            });
        });
    }, []);

    const onDrop = (e) => {
        e.preventDefault();

        const rawNew = e.dataTransfer.getData("application/block");
        const rawExisting = e.dataTransfer.getData("application/script-block");
        let existingPayload = null;

        if (rawExisting) {
            try {
                existingPayload = JSON.parse(rawExisting);
            } catch (err) {
                console.error("Drop payload parse error:", err);
            }
        }

        if (!editableTarget) return;

        const scriptAreaRect = scriptAreaRef.current?.getBoundingClientRect();
        if (!scriptAreaRect) return;

        const dropX = e.clientX - scriptAreaRect.left;
        const dropY = e.clientY - scriptAreaRect.top;
        const scriptAreaWidth = scriptAreaRect.width || DEFAULT_SCRIPTAREA_WIDTH;
        const scriptAreaHeight = scriptAreaRect.height || DEFAULT_SCRIPTAREA_HEIGHT;
        const clampX = (value) => Math.max(0, Math.min(value, scriptAreaWidth));
        const clampY = (value) => Math.max(0, Math.min(value, scriptAreaHeight));

        if (
            rawExisting &&
            existingPayload?.parentContainerId &&
            existingPayload.parentContainerId !== "main"
        ) {
            try {
                dispatch(pushUndoState(histPayload));

                const insertIndex = editableTarget?.scripts?.length || 0;
                if (editableTarget.type === "simulatorRobot") {
                    dispatch(moveBlockBetweenContainers({
                        robotId: editableTarget.id,
                        fromContainerId: existingPayload.parentContainerId,
                        toContainerId: "main",
                        blockId: existingPayload.blockId,
                        toIndex: insertIndex,
                    }));
                    dispatch(updateBlockPosition({
                        robotId: editableTarget.id,
                        blockId: existingPayload.blockId,
                        x: clampX(dropX),
                        y: clampY(dropY),
                        viewportWidth: scriptAreaWidth,
                        viewportHeight: scriptAreaHeight,
                    }));
                } else {
                    dispatch(moveBlockBetweenContainers({
                        actorId: editableTarget.id,
                        fromContainerId: existingPayload.parentContainerId,
                        toContainerId: "main",
                        blockId: existingPayload.blockId,
                        toIndex: insertIndex,
                    }));
                    dispatch(updateBlockPosition({
                        actorId: editableTarget.id,
                        blockId: existingPayload.blockId,
                        x: clampX(dropX),
                        y: clampY(dropY),
                        viewportWidth: scriptAreaWidth,
                        viewportHeight: scriptAreaHeight,
                    }));
                }
            } catch (err) {
                console.error("Drop error:", err);
            }

            setReorderDragging(false);
            setDraggedBlock(null);
            clearDragPreview();
            return;
        }

        console.log('🔵 Drop at:', dropX, dropY);

        // MOVE EXISTING BLOCK CHAIN
        if (rawExisting) {
            try {
                if (!existingPayload) {
                    setReorderDragging(false);
                    setDraggedBlock(null);
                    clearDragPreview();
                    return;
                }

                const payload = existingPayload;
                const scriptsList = editableTarget?.scripts || [];
                const sourceIndex = payload.index;

                // 🔥 ELSE: Move entire chain (position change, not reorder)
                const chainBlockIds = Array.isArray(payload.chainBlockIds) && payload.chainBlockIds.length
                    ? payload.chainBlockIds
                    : [payload.blockId].filter(Boolean);

                const anchorBlockId = payload.blockId || scriptsList[sourceIndex]?.id;
                const anchorBlock = scriptsList.find(b => b.id === anchorBlockId);

                const baseAnchorX = Number.isFinite(anchorBlock?.x) ? anchorBlock.x : dropX;
                const baseAnchorY = Number.isFinite(anchorBlock?.y) ? anchorBlock.y : dropY;
                const deltaX = dropX - baseAnchorX;
                const deltaY = dropY - baseAnchorY;

                const updates = [];
                chainBlockIds.forEach((blockId) => {
                    const currentBlock = scriptsList.find(b => b?.id === blockId);
                    if (!currentBlock) return;

                    const nextX = clampX((currentBlock.x ?? 0) + deltaX);
                    const nextY = clampY((currentBlock.y ?? 0) + deltaY);
                    updates.push({ blockId, x: nextX, y: nextY });
                });

                const chainIdSet = new Set(chainBlockIds.filter(Boolean));
                const chainHeadId = chainBlockIds.find(Boolean);
                const chainTailId = chainBlockIds.length ? chainBlockIds[chainBlockIds.length - 1] : null;
                const chainHeadBlock = chainHeadId ? scriptsList.find(b => b.id === chainHeadId) : anchorBlock;
                const chainTailBlock = chainTailId ? scriptsList.find(b => b.id === chainTailId) : null;

                const anchorIsFixedStart = isFixedStartBlock(anchorBlock);
                const chainHeadIsStart = isFixedStartBlock(chainHeadBlock);
                const chainTailIsEnd = isEndBlock(chainTailBlock);
                const snapTarget = anchorBlock
                    ? findSnapTarget(
                        scriptsList,
                        anchorBlock,
                        dropX,
                        dropY,
                        chainBlockIds,
                        blockPositions,
                        { chainHeadIsStart, chainTailIsEnd }
                    )
                    : null;

                const targetBlockId = snapTarget?.targetBlockId || snapTarget?.targetBlock?.id;
                const targetBlock = targetBlockId ? scriptsList.find(b => b.id === targetBlockId) : null;
                const targetNextId = targetBlock?.nextBlockId;

                let structureSnapshotTaken = false;
                const ensureUndoSnapshot = () => {
                    if (!structureSnapshotTaken) {
                        dispatch(pushUndoState(histPayload));
                        structureSnapshotTaken = true;
                    }
                };
                const disconnectFromBlock = (blockId) => {
                    if (!blockId) return;
                    ensureUndoSnapshot();
                    dispatch(disconnectBlock({ ...histPayload, blockId }));
                };
                const reconnectBlocks = (leftBlockId, rightBlockId) => {
                    if (!leftBlockId || !rightBlockId) return;
                    ensureUndoSnapshot();
                    dispatch(connectBlocks({ ...histPayload, leftBlockId, rightBlockId }));
                };
                const connectBlocksForTarget = (leftBlockId, rightBlockId) => {
                    if (!leftBlockId || !rightBlockId) return;
                    ensureUndoSnapshot();
                    dispatch(connectBlocks({ ...histPayload, leftBlockId, rightBlockId }));
                };

                const previousLeft = chainHeadId
                    ? scriptsList.find(b => b.nextBlockId === chainHeadId)
                    : null;
                if (previousLeft && !chainIdSet.has(previousLeft.id)) {
                    disconnectFromBlock(previousLeft.id);
                }

                const tailNextId = chainTailBlock?.nextBlockId;
                if (chainTailId && tailNextId && !chainIdSet.has(tailNextId)) {
                    disconnectFromBlock(chainTailId);
                }

                if (
                    previousLeft &&
                    tailNextId &&
                    !chainIdSet.has(previousLeft.id) &&
                    !chainIdSet.has(tailNextId)
                ) {
                    reconnectBlocks(previousLeft.id, tailNextId);
                }

                let finalUpdates;
                if (
                    snapTarget &&
                    chainHeadId &&
                    chainTailId &&
                    targetBlock &&
                    !chainIdSet.has(targetBlock.id)
                ) {
                    finalUpdates = alignUpdatesToSnap(
                        updates,
                        chainHeadId,
                        snapTarget.snapX,
                        snapTarget.snapY,
                        clampX,
                        clampY
                    );

                    if (snapTarget.snapPosition === 'right') {
                        connectBlocksForTarget(targetBlock.id, chainHeadId);
                        if (targetNextId && !chainIdSet.has(targetNextId)) {
                            connectBlocksForTarget(chainTailId, targetNextId);
                        } else if (chainTailId) {
                            disconnectFromBlock(chainTailId);
                        }
                    } else if (snapTarget.snapPosition === 'left') {
                        connectBlocksForTarget(chainTailId, targetBlock.id);
                    }
                } else if (!anchorIsFixedStart) {
                    const snapped = snapUpdatesHorizontally(
                        updates,
                        scriptsList,
                        chainBlockIds,
                        dropX,
                        dropY,
                        clampX,
                        clampY,
                        blockPositions
                    );
                    finalUpdates = snapped && snapped.length ? snapped : updates;
                } else {
                    finalUpdates = updates;
                }

                finalUpdates.forEach(({ blockId, x, y }) => {
                    const payload = {
                        blockId,
                        x,
                        y,
                        viewportWidth: scriptAreaWidth,
                        viewportHeight: scriptAreaHeight,
                    };
                    if (editableTarget.type === "simulatorRobot") {
                        payload.robotId = editableTarget.id;
                    } else {
                        payload.actorId = editableTarget.id;
                    }
                    dispatch(updateBlockPosition(payload));
                });

            } catch (err) {
                console.error('Drop error:', err);
            }

            setReorderDragging(false);
            clearDragPreview();
            return;
        } else if (rawNew) {
            try {
                const block = JSON.parse(rawNew);

                block.x = clampX(dropX);
                block.y = clampY(dropY);

                const scriptsList = editableTarget?.scripts || [];
                const snapTarget = findSnapTarget(
                    scriptsList,
                    block,
                    dropX,
                    dropY,
                    [],
                    blockPositions,
                    { chainHeadIsStart: isFixedStartBlock(block), chainTailIsEnd: isEndBlock(block) }
                );
                if (snapTarget) {
                    block.x = snapTarget.snapX;
                    block.y = snapTarget.snapY;
                } else {
                    const snappedNewPosition = snapUpdatesHorizontally(
                        [{ blockId: '__new__', x: block.x, y: block.y, blockOverride: block }],
                        scriptsList,
                        ['__new__'],
                        dropX,
                        dropY,
                        clampX,
                        clampY,
                        blockPositions
                    );
                    if (snappedNewPosition && snappedNewPosition[0]) {
                        block.x = snappedNewPosition[0].x;
                        block.y = snappedNewPosition[0].y;
                    }
                }

                const normalizedWidth = scriptAreaWidth || DEFAULT_SCRIPTAREA_WIDTH;
                const normalizedHeight = scriptAreaHeight || DEFAULT_SCRIPTAREA_HEIGHT;
                block.x = Math.max(0, Math.min(block.x ?? 0, normalizedWidth));
                block.y = Math.max(0, Math.min(block.y ?? 0, normalizedHeight));
                block.layoutRefWidth = normalizedWidth;
                block.layoutRefHeight = normalizedHeight;
                block.xRatio = normalizedWidth ? block.x / normalizedWidth : 0;
                block.yRatio = normalizedHeight ? block.y / normalizedHeight : 0;

                if (block.type === 'video_transparency' && !block.hasOwnProperty('opacity')) {
                    block.opacity = 100;
                }

                const existingIds = new Set((scriptsList || []).map(b => b.id));

                dispatch(pushUndoState(histPayload));

                if (editableTarget.type === "simulatorRobot") {
                    dispatch(addBlockToSimulatorScript({
                        robotId: editableTarget.id,
                        block,
                        index: editableTarget.scripts?.length || 0
                    }));
                } else {
                    dispatch(addBlockToScript({
                        actorId: editableTarget.id,
                        block,
                        index: editableTarget.scripts?.length || 0
                    }));
                }

                console.log('?o. New block added at:', block.x, block.y);

                if (snapTarget) {
                    const updatedScripts = getLatestScriptsForTarget();
                    const addedBlock = updatedScripts.find(b => !existingIds.has(b.id));
                    if (addedBlock) {
                        const targetBlockId = snapTarget.targetBlockId || snapTarget.targetBlock?.id;
                        const targetBlock = targetBlockId
                            ? updatedScripts.find(b => b.id === targetBlockId)
                            : null;

                        if (targetBlock) {
                            const targetNextId = targetBlock.nextBlockId;
                            if (snapTarget.snapPosition === 'right') {
                                dispatch(connectBlocks({
                                    ...histPayload,
                                    leftBlockId: targetBlock.id,
                                    rightBlockId: addedBlock.id
                                }));
                                if (targetNextId && targetNextId !== addedBlock.id) {
                                    dispatch(connectBlocks({
                                        ...histPayload,
                                        leftBlockId: addedBlock.id,
                                        rightBlockId: targetNextId
                                    }));
                                } else {
                                    dispatch(disconnectBlock({
                                        ...histPayload,
                                        blockId: addedBlock.id
                                    }));
                                }
                            } else if (snapTarget.snapPosition === 'left') {
                                dispatch(connectBlocks({
                                    ...histPayload,
                                    leftBlockId: addedBlock.id,
                                    rightBlockId: targetBlock.id
                                }));
                            }
                        }
                    }
                }

            } catch (err) {
                console.error("? Drop failed:", err);
            }
        }

        setReorderDragging(false);
        setDraggedBlock(null);
        clearDragPreview();
    };


    const onDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
    };

    // Allow dragging existing blocks to reorder within the chain (start/end remain fixed).
    const handleBlockDragStart = (e, block, index, parentContainerId = "main") => {
        if (!block) return;

        setReorderDragging(true);

        const scriptsList = editableTarget?.scripts || [];
        const chainInfo = getDragChain(scriptsList, block);
        const chainBlockIds = chainInfo.blocks.length
            ? chainInfo.blocks.map(b => b.id)
            : [block.id];

        console.log('dY"- Dragging chain:', chainBlockIds.length, 'blocks');
        if (parentContainerId === "main") {
            startDragPreview(chainBlockIds, block.id, e?.clientX, e?.clientY);
        }

        e.dataTransfer.setData("application/script-block", JSON.stringify({
            actorId: editableTarget?.id,
            index,
            blockId: block.id,
            parentContainerId,
            chainBlockIds
        }));
        e.dataTransfer.setData("text/plain", "reorder");
        e.dataTransfer.effectAllowed = "move";
    };

    const touchDragStateRef = useRef(null);

    const handleBlockTouchStart = (e, block, index, parentContainerId = "main") => {
        if (!e || !block) return;

        const touch = e.touches?.[0];
        const scriptsList = editableTarget?.scripts || [];
        const chainInfo = getDragChain(scriptsList, block);
        const chainBlockIds = chainInfo.blocks.length
            ? chainInfo.blocks.map(b => b.id)
            : [block.id];

        touchDragStateRef.current = {
            block,
            index,
            chainLength: chainInfo.length,
            chainBlockIds,
            startX: touch?.clientX || 0,
            startY: touch?.clientY || 0,
            target: e.currentTarget,
            startedDrag: false,
            blockId: block.id,
            parentContainerId
        };
    };


    const handleBlockTouchMove = (e, block, index) => {
        const state = touchDragStateRef.current;
        if (!state || state.block !== block || state.index !== index) return;

        const touch = e.touches?.[0];
        if (!touch) return;

        const dx = Math.abs(touch.clientX - state.startX);
        const dy = Math.abs(touch.clientY - state.startY);

        if (!state.startedDrag && (dx > 6 || dy > 6)) {
            state.startedDrag = true;
            setReorderDragging(true);
            if (state.parentContainerId === "main") {
                startDragPreview(state.chainBlockIds, state.blockId, touch.clientX, touch.clientY);
            }

            startTouchDrag(e, {
                dataTransferData: {
                    "application/script-block": JSON.stringify({
                        actorId: editableTarget?.id,
                        index,
                        chainLength: state.chainLength,  // 🔥 NEW: Include chain length
                        chainBlockIds: state.chainBlockIds,
                        blockId: state.blockId,
                        parentContainerId: state.parentContainerId
                    }),
                    "text/plain": "reorder"
                },
                previewElement: state.target,
                dropEffect: "move",
                onMove: ({ clientX, clientY }) => scheduleDragPreviewUpdate(clientX, clientY),
                onEnd: ({ clientX, clientY }) =>
                    handleBlockDragEnd({ clientX, clientY }, block, index, state.parentContainerId)
            });

            touchDragStateRef.current = null;
        }

        if (state.startedDrag) {
            e.preventDefault();
        }
    };

    const shouldRunOnTap = (block) => {
        if (!block) return false;
        const tappableCategories = new Set(['motion', 'control', 'looks', 'stembot']);
        return tappableCategories.has(block.category);
    };

    const handleBlockTouchEnd = (e, block, index) => {
        const state = touchDragStateRef.current;
        if (!state || state.block !== block || state.index !== index) {
            touchDragStateRef.current = null;
            return;
        }
        if (!state.startedDrag) {
            touchDragStateRef.current = null;
            if (shouldRunOnTap(block)) {
                handleRunClick();
            } else {
                clickBlock(block, e);
            }
        }
    };

    const handleBlockDragEnd = (e, block, index, parentContainerId = "main") => {
        clearDragPreview();
        // Remove if dropped outside script area (except start/end)
        const rect = scriptAreaRef.current?.getBoundingClientRect();

        if (rect && block && !isFixedStartBlock(block) && block.category !== 'end') {
            const inside =
                e.clientX >= rect.left &&
                e.clientX <= rect.right &&
                e.clientY >= rect.top &&
                e.clientY <= rect.bottom;

            if (!inside && editableTarget) {
                if (parentContainerId !== "main") {
                    dispatch(pushUndoState(histPayload));

                    if (editableTarget.type === "simulatorRobot") {
                        dispatch(removeBlockFromContainer({
                            robotId: editableTarget.id,
                            containerId: parentContainerId,
                            blockId: block.id
                        }));
                    } else {
                        dispatch(removeBlockFromContainer({
                            actorId: editableTarget.id,
                            containerId: parentContainerId,
                            blockId: block.id
                        }));
                    }

                    setReorderDragging(false);
                    return;
                }

                const scriptsList = editableTarget.scripts;
                if (index >= 0 && index < scriptsList.length) {
                    dispatch(pushUndoState(histPayload));

                    // 🔥 NEW: Calculate chain and remove all blocks
                    const anchorBlock = scriptsList[index];
                    const chain = calculateBlockChain(scriptsList, anchorBlock);
                    const chainBlocks = chain.blocks.length ? chain.blocks : (anchorBlock ? [anchorBlock] : []);
                    const chainIds = new Set(chainBlocks.map(b => b.id));
                    const chainHeadId = chainBlocks[0]?.id;
                    const chainTailId = chainBlocks[chainBlocks.length - 1]?.id;
                    const previousLeft = chainHeadId
                        ? scriptsList.find(b => b?.nextBlockId === chainHeadId)
                        : null;
                    const chainTailBlock = chainTailId
                        ? scriptsList.find(b => b?.id === chainTailId)
                        : null;
                    const tailNextId = chainTailBlock?.nextBlockId || null;

                    const layoutPositions = computeBlockPositions(
                        scriptsList,
                        scriptAreaSize?.width || DEFAULT_SCRIPTAREA_WIDTH,
                        scriptAreaSize?.height || DEFAULT_SCRIPTAREA_HEIGHT
                    );

                    const remaining = scriptsList
                        .filter(block => !chainIds.has(block?.id))
                        .map(block => (block ? { ...block } : block));

                    const remainingById = new Map(
                        remaining.filter(Boolean).map(block => [block.id, block])
                    );

                    // Clear any dangling links into the removed chain.
                    remaining.forEach((remainingBlock) => {
                        if (!remainingBlock) return;
                        if (chainIds.has(remainingBlock.nextBlockId)) {
                            remainingBlock.nextBlockId = null;
                        }
                    });

                    // Reconnect left neighbor to the block after the removed chain (if valid).
                    if (
                        previousLeft &&
                        !chainIds.has(previousLeft.id) &&
                        tailNextId &&
                        !chainIds.has(tailNextId)
                    ) {
                        const previousRemaining = remainingById.get(previousLeft.id);
                        if (previousRemaining) {
                            previousRemaining.nextBlockId = tailNextId;
                        }
                    }

                    const baseIncomingIds = new Set();
                    remaining.forEach((remainingBlock) => {
                        if (!remainingBlock?.nextBlockId) return;
                        baseIncomingIds.add(remainingBlock.nextBlockId);
                    });

                    // If the removed block had no tail connection but a visible neighbor exists,
                    // reconnect to the nearest valid head based on positions.
                    const previousRemaining = previousLeft
                        ? remainingById.get(previousLeft.id)
                        : null;
                    if (previousRemaining && !previousRemaining.nextBlockId && !isEndBlock(previousRemaining)) {
                        const prevPos = layoutPositions.get(previousRemaining.id)
                            || (Number.isFinite(previousRemaining.x) && Number.isFinite(previousRemaining.y)
                                ? { x: previousRemaining.x, y: previousRemaining.y }
                                : null);
                        if (prevPos) {
                            const SNAP_DISTANCE = 80;
                            let bestCandidate = null;
                            let bestDistance = Infinity;

                            remaining.forEach((candidate) => {
                                if (!candidate || candidate.id === previousRemaining.id) return;
                                if (baseIncomingIds.has(candidate.id)) return;
                                if (isFixedStartBlock(candidate)) return;

                                const candidatePos = layoutPositions.get(candidate.id)
                                    || (Number.isFinite(candidate.x) && Number.isFinite(candidate.y)
                                        ? { x: candidate.x, y: candidate.y }
                                        : null);
                                if (!candidatePos) return;

                                const expectedX = prevPos.x
                                    + getBlockRightSnapOffset(previousRemaining)
                                    - getBlockLeftSnapInset(candidate);
                                const expectedY = prevPos.y
                                    + getBlockRightConnectorOffsetY(previousRemaining)
                                    - getBlockLeftConnectorOffsetY(candidate);
                                const distance = Math.hypot(candidatePos.x - expectedX, candidatePos.y - expectedY);

                                if (distance < SNAP_DISTANCE && distance < bestDistance) {
                                    bestDistance = distance;
                                    bestCandidate = candidate;
                                }
                            });

                            if (bestCandidate) {
                                previousRemaining.nextBlockId = bestCandidate.id;
                                bestCandidate.x = null;
                                bestCandidate.y = null;
                            }
                        }
                    }

                    // Ensure newly exposed heads keep a stable position.
                    const incomingIds = new Set();
                    remaining.forEach((remainingBlock) => {
                        if (!remainingBlock?.nextBlockId) return;
                        incomingIds.add(remainingBlock.nextBlockId);
                    });

                    const layoutWidth = scriptAreaSize?.width || DEFAULT_SCRIPTAREA_WIDTH;
                    const layoutHeight = scriptAreaSize?.height || DEFAULT_SCRIPTAREA_HEIGHT;
                    remaining.forEach((remainingBlock) => {
                        if (!remainingBlock || incomingIds.has(remainingBlock.id)) return;
                        if (hasStoredPosition(remainingBlock)) return;
                        const pos = layoutPositions.get(remainingBlock.id);
                        if (!pos) return;
                        remainingBlock.x = pos.x;
                        remainingBlock.y = pos.y;
                        remainingBlock.layoutRefWidth = layoutWidth;
                        remainingBlock.layoutRefHeight = layoutHeight;
                        remainingBlock.xRatio = layoutWidth ? pos.x / layoutWidth : 0;
                        remainingBlock.yRatio = layoutHeight ? pos.y / layoutHeight : 0;
                    });

                    if (editableTarget.type === "simulatorRobot") {
                        dispatch(clearSimulatorScript({ robotId: editableTarget.id }));
                        remaining.forEach((b, i) =>
                            dispatch(addBlockToSimulatorScript({ robotId: editableTarget.id, block: b, index: i }))
                        );
                    } else {
                        dispatch(clearScript({ actorId: editableTarget.id }));
                        remaining.forEach((b, i) =>
                            dispatch(addBlockToScript({ actorId: editableTarget.id, block: b, index: i }))
                        );
                    }
                }
            }
        }

        setReorderDragging(false);
    };


    const handleScriptAreaDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = reorderDragging ? "move" : "copy";
        if (dragPreview.active) {
            scheduleDragPreviewUpdate(e.clientX, e.clientY);
        }
    };

    useEffect(() => {
        if (!dragPreview.active) return;
        const handleWindowDrag = (e) => {
            if (!e) return;
            scheduleDragPreviewUpdate(e.clientX, e.clientY);
        };
        const handleWindowDragOver = (e) => {
            if (!e) return;
            scheduleDragPreviewUpdate(e.clientX, e.clientY);
        };
        const handleWindowCancel = () => {
            clearDragPreview();
        };
        window.addEventListener("drag", handleWindowDrag);
        window.addEventListener("dragover", handleWindowDragOver);
        window.addEventListener("dragend", handleWindowCancel);
        window.addEventListener("drop", handleWindowCancel);
        window.addEventListener("mouseup", handleWindowCancel);
        window.addEventListener("touchend", handleWindowCancel, { passive: true });
        window.addEventListener("touchcancel", handleWindowCancel, { passive: true });
        return () => {
            window.removeEventListener("drag", handleWindowDrag);
            window.removeEventListener("dragover", handleWindowDragOver);
            window.removeEventListener("dragend", handleWindowCancel);
            window.removeEventListener("drop", handleWindowCancel);
            window.removeEventListener("mouseup", handleWindowCancel);
            window.removeEventListener("touchend", handleWindowCancel);
            window.removeEventListener("touchcancel", handleWindowCancel);
        };
    }, [dragPreview.active, scheduleDragPreviewUpdate, clearDragPreview]);

    useEffect(() => {
        const node = scriptAreaRef.current;
        if (!node) return;
        const handleTouchDrop = (evt) => {
            if (!evt.detail) return;
            onDrop({
                preventDefault: () => evt.preventDefault(),
                dataTransfer: evt.detail.dataTransfer,
                clientX: evt.detail.clientX,
                clientY: evt.detail.clientY,
            });
        };
        node.addEventListener(TOUCH_DROP_EVENT, handleTouchDrop);
        return () => {
            node.removeEventListener(TOUCH_DROP_EVENT, handleTouchDrop);
        };
    }, [onDrop]);

    const closeLanguagePicker = () => {
        setLanguagePicker((prev) => ({ ...prev, open: false, blockId: null }));
    };

    const handleLanguageSelect = (langOption) => {
        if (!editableTarget || !languagePicker.blockId) {
            closeLanguagePicker();
            return;
        }

        dispatch(pushUndoState(histPayload));

        const payload = {
            blockId: languagePicker.blockId,
            data: {
                selectedLang: langOption.code,
                selectedLangLabel: langOption.label,
            }
        };

        if (editableTarget.type === 'simulatorRobot') {
            payload.robotId = editableTarget.id;
        } else {
            payload.actorId = editableTarget.id;
        }

        dispatch(updateBlockCustomData(payload));
        setLanguagePicker({
            open: false,
            blockId: null,
            position: { top: 0, left: 0 },
            selected: langOption.code,
        });
    };

    // Handling block clicks (configuration)
    const clickBlock = async (block, e) => {
        if (!block) return;

        // compute bounding rect for the clicked element so picker appears next to it
        let rect;
        try {
            rect = e?.currentTarget?.getBoundingClientRect();
        } catch (err) {
            rect = null;
        }

        if (block.type === "setPathColor") {
            openColorPicker(block, e, "pathColor");
            return;
        }

        if (block.type === "robot_led_ring_index") {
            openColorPicker(block, e, "ledRingColor");
            return;
        }

        if (block.type === "colorDetected") {
            openColorPicker(block, e, "targetColor");
            return;
        }

        // special-case: Start on Green Flag -> run logic
        // special-case: Start on Green Flag -> run logic
        // special-case: Start on Green Flag -> run logic
        // special-case: Start on Green Flag -> run logic
        if (block.category === "start" && block.name === "Start on Green Flag") {
            if (isScriptRunning) {
                console.warn("Script already running - ignoring start block click.");
                return;
            }

            const target = editableTarget;
            if (!target) {
                console.warn("No editable target available for start block run.");
                return;
            }

            try {
                textToSpeechManager.unlockAudio();
                setIsScriptRunning(true);
                const runOptions = block.id ? { startBlockIds: [block.id] } : undefined;
                await run(target, dispatch, scene?.sounds, target.id, runOptions);
            } catch (err) {
                console.error("Error while running script:", err);
            } finally {
                setIsScriptRunning(false);  // ?o. Always reset
            }
            return;
        }

        closeLanguagePicker();

        if (block.category === "start") return;

        const skipPickerBlocks = new Set(["Go To Random Position"]);
        if (skipPickerBlocks.has(block.name)) {
            return;
        }

        const noPickerTypes = new Set([
            "aiGetResponse",
            "aiResponse",
            "speechToText",
            "aiSetPersona",
            "aiHighlightPos"
        ]);
        if (noPickerTypes.has(block.type)) {
            return;
        }

        // For Wait block, show the number picker
        if (block.name === "Wait") {
            setTapBlock(block);
            if (rect) {
                setPickerPos({
                    top: rect.bottom + window.scrollY + 8, // 8px gap
                    left: rect.left + window.scrollX,
                });
            } else {
                setPickerPos({ top: window.innerHeight / 2, left: window.innerWidth / 2 });
            }
            setPickerOpen(true);
            return;
        }

        // Speed/Sound blocks don't need configuration
        if (
            block.name === "Speed"
            || block.name === "Set Servo Speed"
            || block.type === "sound_effect"
            || block.type === "robot_sound_effect"
        ) {
            return;
        }

        // For Video Transparency block, show the number picker
        if (block.name === "Set Video Transparency") {
            setTapBlock(block);
            if (rect) {
                setPickerPos({
                    top: rect.bottom + window.scrollY + 8,
                    left: rect.left + window.scrollX,
                });
            } else {
                setPickerPos({ top: window.innerHeight / 2, left: window.innerWidth / 2 });
            }
            setPickerOpen(true);
            return;
        }

        // For Say/Speak block, open the dialog for text input
        if (block.name === "Say" || block.type === "say" || block.type === "ttsSpeak" || block.type === "aiPrompt") {
            const startingValue = block.message
                || block?.parameters?.message
                || block?.value
                || "hi";
            setTextModal({
                open: true,
                blockId: block.id,
                value: startingValue,
                title: block.type === "ttsSpeak"
                    ? "Enter speech text"
                    : block.type === "aiPrompt"
                        ? "Enter prompt"
                        : "Enter message"
            });
            return;
        }

        if (block.type === "scriptText") {
            const startingValue = typeof block.scriptText === "string" ? block.scriptText : "";
            setScriptTextModal({
                open: true,
                blockId: block.id,
                value: startingValue
            });
            return;
        }
        // For other blocks with counts, show number picker
        setTapBlock(block);
        if (rect) {
            setPickerPos({
                top: rect.bottom + window.scrollY + 8,
                left: rect.left + window.scrollX,
            });
        } else {
            setPickerPos({ top: window.innerHeight / 2, left: window.innerWidth / 2 });
        }
        setPickerOpen(true);
    };

    const setCount = (n, blockOverride = null) => {
        if (!editableTarget) return;
        const targetBlock = blockOverride || tapBlock;
        if (!targetBlock) return;

        const safeValue = clampValueForBlock(targetBlock, Number.isFinite(n) ? n : 1);

        // snapshot before change
        dispatch(pushUndoState(histPayload));

        if (editableTarget.type === 'simulatorRobot') {
            dispatch(updateSimulatorBlockCount({
                robotId: editableTarget.id,
                blockId: targetBlock.id,
                newCount: safeValue,
                property: targetBlock.name === "Set Video Transparency" ? 'opacity' : 'count'
            }));
        } else if (targetBlock.name === "Set Video Transparency") {
            dispatch(updateBlockCount({
                actorId: editableTarget.id,
                blockId: targetBlock.id,
                newCount: safeValue,
                property: 'opacity'
            }));
        } else {
            dispatch(updateBlockCount({
                actorId: editableTarget.id,
                blockId: targetBlock.id,
                newCount: safeValue
            }));
        }
    };

    const handleOpacityChange = (blockId, newOpacity) => {
        if (!editableTarget) return;

        // snapshot before change (so opacity edits are undoable)
        dispatch(pushUndoState(histPayload));

        if (editableTarget.type === 'simulatorRobot') {
            dispatch(updateSimulatorBlockCount({ robotId: editableTarget.id, blockId, newCount: newOpacity, property: 'opacity' }));
        } else {
            dispatch(updateBlockCount({ actorId: editableTarget.id, blockId, newCount: newOpacity, property: 'opacity' }));
        }
    };

    // Run button: respects either selected simulator robot (if selected) or stage actor
    // Run button: respects either selected simulator robot (if selected) or stage actor
    const handleRunClick = async () => {
        if (isScriptRunning) {
            console.warn("?? Script already running! Ignoring run request.");
            return;
        }

        try {
            setIsScriptRunning(true);
            const currentState = store.getState();
            const isSimulatorVisible = document.querySelector('.simulator-robot') !== null;
            const simRobots = currentState.scene?.simulatorRobots || [];

            if (isSimulatorVisible && simRobots.length > 0) {
                if (selectedSimRobot) {
                    if (selectedSimRobot.scripts && selectedSimRobot.scripts.length > 0) {
                        console.log('?? Running selected simulator robot script:', selectedSimRobot.name);
                        const greenIds = findStartBlockIds(selectedSimRobot.scripts, block => block.name === "Start on Green Flag");
                        const runOptions = greenIds.length ? { startBlockIds: greenIds } : undefined;
                        await run(selectedSimRobot, dispatch, scene?.sounds, selectedSimRobot.id, runOptions);
                    } else {
                        console.warn('?? Selected simulator robot has no scripts:', selectedSimRobot);
                    }
                } else {
                    for (const robot of simRobots) {
                        if (robot.scripts && robot.scripts.length > 0) {
                            console.log('?? Running simulator robot script:', robot.name);
                            const greenIds = findStartBlockIds(robot.scripts, block => block.name === "Start on Green Flag");
                            const runOptions = greenIds.length ? { startBlockIds: greenIds } : undefined;
                            await run(robot, dispatch, scene?.sounds, robot.id, runOptions);
                        } else {
                            console.warn('?? Simulator robot has no scripts to run:', robot.name);
                        }
                    }
                }
            } else if (stageActor) {
                console.log('?? Running stage actor script:', stageActor.name);
                const greenIds = findStartBlockIds(stageActor.scripts, block => block.name === "Start on Green Flag");
                const runOptions = greenIds.length ? { startBlockIds: greenIds } : undefined;
                await run(stageActor, dispatch, scene?.sounds, stageActor.id, runOptions);
            } else {
                console.log('? No actor or robot available to run');
            }
        } catch (err) {
            console.error('Error while running script:', err);
        } finally {
            setIsScriptRunning(false);
        }
    };

    const scripts = editableTarget?.scripts || [];
    const blockPositions = useMemo(
        () => computeBlockPositions(scripts, scriptAreaSize.width, scriptAreaSize.height),
        [scripts, scriptAreaSize.width, scriptAreaSize.height]
    );
    const targetId = editableTarget?.id;
    const targetType = editableTarget?.type;
    useEffect(() => {
        if (!targetId) return;
        const width = scriptAreaSize.width || DEFAULT_SCRIPTAREA_WIDTH;
        const height = scriptAreaSize.height || DEFAULT_SCRIPTAREA_HEIGHT;
        if (!width || !height) return;
        const needsUpdate = scripts.filter(blockNeedsNormalization);
        if (!needsUpdate.length) return;
        needsUpdate.forEach(block => {
            if (!block?.id) return;
            const payload = {
                blockId: block.id,
                x: Number.isFinite(block?.x) ? block.x : MIN_START_X,
                y: Number.isFinite(block?.y) ? block.y : 50,
                viewportWidth: width,
                viewportHeight: height,
            };
            if (targetType === "simulatorRobot") {
                payload.robotId = targetId;
            } else {
                payload.actorId = targetId;
            }
            dispatch(updateBlockPosition(payload));
        });
    }, [dispatch, targetId, targetType, scripts, scriptAreaSize.width, scriptAreaSize.height]);
    const resolveBlockPosition = (block) => {
        if (!block) {
            return { x: 50, y: 50 };
        }
        const computed = blockPositions.get(block.id);
        const fallbackX = Number.isFinite(block.x) ? block.x : 50;
        const fallbackY = Number.isFinite(block.y) ? block.y : 50;
        return {
            x: Number.isFinite(computed?.x) ? computed.x : fallbackX,
            y: Number.isFinite(computed?.y) ? computed.y : fallbackY,
        };
    };
    const resolvePreviewBlockPosition = useCallback((block) => {
        const base = resolveBlockPosition(block);
        const deltaX = dragPreview.deltaX || 0;
        const deltaY = dragPreview.deltaY || 0;
        return {
            x: (base?.x || 0) + deltaX,
            y: (base?.y || 0) + deltaY,
        };
    }, [resolveBlockPosition, dragPreview.deltaX, dragPreview.deltaY]);

    if (!editableTarget) {
        return (
            <div className="scriptarea-root">
                <div className="scriptarea-buttons">
                    <button className="s-btn run" disabled>▶ Run</button>
                    <button className="s-btn clear" disabled>🗑 Clear</button>
                </div>
                <div className="scriptarea-noborder">
                    <div className="s-hint">Select an actor (stage) or a robot (simulator) to edit scripts</div>
                </div>
            </div>
        );
    }


    return (
        <div className="scriptarea-root">
            <div className="scriptarea-buttons">
                {/* <button
                    className="s-btn run"
                    onClick={handleRunClick}
                    disabled={!scripts.length}
                >
                    ▶
                </button> */}
                {/* <button
                    className="s-btn clear"
                    onClick={() => {
                        // snapshot before change
                        dispatch(pushUndoState(histPayload));

                        if (editableTarget.type === 'simulatorRobot') {
                            dispatch(clearSimulatorScript({ robotId: editableTarget.id }));
                        } else {
                            dispatch(clearScript({ actorId: editableTarget.id }));
                        }

                        // Turn global camera state OFF
                        dispatch(setCameraState('off'));
                        // Also stop the actual camera controller if present (side-effect in component)
                        if (typeof window !== 'undefined') {
                            window.humanDetectionController?.stopCamera?.();
                        }
                    }}
                    disabled={!scripts.length}
                >
                    🗑
                </button> */}

            </div>
            <div
                className="scriptarea-noborder"
                ref={scriptAreaRef}
                onDrop={onDrop}
                onDragOver={handleScriptAreaDragOver}
                data-touch-drop-target="script-area"
            >
                <div className="script-chain">
                    {scripts.length === 0 ? (
                        <div className="s-hint">Drop blocks here to build your script</div>
                    ) : (
                        scripts.map((b, i) => {
                            if (b.isContainer || b.type === "repeat") {
                                return (
                                    <BlockRenderer
                                        key={b.id}
                                        block={b}
                                        index={i}
                                        parentContainerId="main"
                                        editableTarget={editableTarget}
                                        inlineEdit={inlineEdit}
                                        dispatch={dispatch}
                                        histPayload={histPayload}
                                        clickBlock={clickBlock}
                                        handleBadgeClick={handleBadgeClick}
                                        handleBadgeDoubleClick={handleBadgeDoubleClick}
                                        handleBlockDragStart={handleBlockDragStart}
                                        handleBlockDragEnd={handleBlockDragEnd}
                                        handleBlockTouchStart={handleBlockTouchStart}
                                        handleBlockTouchMove={handleBlockTouchMove}
                                        handleBlockTouchEnd={handleBlockTouchEnd}
                                        renderInlineNumberInput={renderInlineNumberInput}
                                        renderInlineTextInput={renderInlineTextInput}
                                        globalCameraState={globalCameraState}
                                        isScriptRunning={isScriptRunning}
                                        handleRunClick={handleRunClick}
                                        openColorPicker={openColorPicker}
                                        handlePinBlockClick={handlePinBlockClick}
                                        resolveBlockPosition={resolveBlockPosition}
                                        draggedBlock={draggedBlock}
                                        dragPreviewActive={dragPreview.active}
                                        dragPreviewIdSet={dragPreviewIdSet}
                                        dragPreviewReady={dragPreview.ready}
                                        languageOptions={languageDropdownOptions}
                                    />
                                );
                            }

                            return (
                                <ScriptBlock
                                    key={b.id}
                                    block={b}
                                    index={i}
                                    parentContainerId="main"
                                    resolveBlockPosition={resolveBlockPosition}
                                    draggedBlock={draggedBlock}
                                    dragPreviewActive={dragPreview.active}
                                    dragPreviewIdSet={dragPreviewIdSet}
                                    dragPreviewReady={dragPreview.ready}
                                    handleBlockDragStart={handleBlockDragStart}
                                    handleBlockDragEnd={handleBlockDragEnd}
                                    handleBlockTouchStart={handleBlockTouchStart}
                                    handleBlockTouchMove={handleBlockTouchMove}
                                    handleBlockTouchEnd={handleBlockTouchEnd}
                                    clickBlock={clickBlock}
                                    handleBadgeClick={handleBadgeClick}
                                    handleBadgeDoubleClick={handleBadgeDoubleClick}
                                    renderInlineNumberInput={renderInlineNumberInput}
                                    renderInlineTextInput={renderInlineTextInput}
                                    globalCameraState={globalCameraState}
                                    isScriptRunning={isScriptRunning}
                                    handleRunClick={handleRunClick}
                                    dispatch={dispatch}
                                    histPayload={histPayload}
                                    editableTarget={editableTarget}
                                    inlineEdit={inlineEdit}
                                    openColorPicker={openColorPicker}
                                    handlePinBlockClick={handlePinBlockClick}
                                    languageOptions={languageDropdownOptions}
                                />
                            );
                        })
                    )}
                </div>
                {dragPreview.active && dragPreview.chainBlockIds.length ? (
                    <div className="scriptarea-drag-overlay" aria-hidden="true">
                        {dragPreview.chainBlockIds.map((blockId) => {
                            const previewBlock = scripts.find((b) => b?.id === blockId);
                            if (!previewBlock) return null;
                            const previewIndex = scripts.findIndex((b) => b?.id === blockId);
                            if (previewBlock.isContainer || previewBlock.type === "repeat") {
                                return (
                                    <BlockRenderer
                                        key={`preview-${previewBlock.id}`}
                                        block={previewBlock}
                                        index={previewIndex}
                                        parentContainerId="main"
                                        editableTarget={editableTarget}
                                        inlineEdit={inlineEdit}
                                        dispatch={dispatch}
                                        histPayload={histPayload}
                                        clickBlock={clickBlock}
                                        handleBadgeClick={handleBadgeClick}
                                        handleBadgeDoubleClick={handleBadgeDoubleClick}
                                        handleBlockDragStart={undefined}
                                        handleBlockDragEnd={undefined}
                                        handleBlockTouchStart={undefined}
                                        handleBlockTouchMove={undefined}
                                        handleBlockTouchEnd={undefined}
                                        renderInlineNumberInput={renderInlineNumberInput}
                                        renderInlineTextInput={renderInlineTextInput}
                                        globalCameraState={globalCameraState}
                                        isScriptRunning={isScriptRunning}
                                        handleRunClick={handleRunClick}
                                        openColorPicker={openColorPicker}
                                        handlePinBlockClick={handlePinBlockClick}
                                        resolveBlockPosition={resolvePreviewBlockPosition}
                                        draggedBlock={null}
                                        dragPreviewActive={false}
                                        dragPreviewIdSet={null}
                                        languageOptions={languageDropdownOptions}
                                    />
                                );
                            }
                            return (
                                <ScriptBlock
                                    key={`preview-${previewBlock.id}`}
                                    block={previewBlock}
                                    index={previewIndex}
                                    parentContainerId="main"
                                    resolveBlockPosition={resolvePreviewBlockPosition}
                                    draggedBlock={null}
                                    dragPreviewActive={false}
                                    dragPreviewIdSet={null}
                                    handleBlockDragStart={undefined}
                                    handleBlockDragEnd={undefined}
                                    handleBlockTouchStart={undefined}
                                    handleBlockTouchMove={undefined}
                                    handleBlockTouchEnd={undefined}
                                    clickBlock={clickBlock}
                                    handleBadgeClick={handleBadgeClick}
                                    handleBadgeDoubleClick={handleBadgeDoubleClick}
                                    renderInlineNumberInput={renderInlineNumberInput}
                                    renderInlineTextInput={renderInlineTextInput}
                                    globalCameraState={globalCameraState}
                                    isScriptRunning={isScriptRunning}
                                    handleRunClick={handleRunClick}
                                    dispatch={dispatch}
                                    histPayload={histPayload}
                                    editableTarget={editableTarget}
                                    inlineEdit={inlineEdit}
                                    openColorPicker={openColorPicker}
                                    handlePinBlockClick={handlePinBlockClick}
                                    languageOptions={languageDropdownOptions}
                                />
                            );
                        })}
                    </div>
                ) : null}
            </div>

            <NumberPicker
                isOpen={pickerOpen}
                onClose={() => setPickerOpen(false)}
                onSelect={(n) => {
                    setCount(n);
                    // optionally close picker after selection:
                    // setPickerOpen(false);
                }}
                currentValue={tapBlock?.name === "Set Video Transparency"
                    ? tapBlock?.opacity
                    : (tapBlock?.count || (tapBlock?.name === "Wait" ? 3 : 1))}
                blockType={tapBlock?.type}
                position={pickerPos}
            />

            <ColorPickerPopover
                open={colorPickerOpen}
                position={colorPickerPos}
                value={colorPickerValue}
                onChange={applyPathColor}
                onClose={closeColorPicker}
            />

            <LanguagePickerPopover
                open={languagePicker.open}
                position={languagePicker.position}
                languages={languageOptions}
                selected={languagePicker.selected}
                onSelect={handleLanguageSelect}
                onClose={closeLanguagePicker}
            />

            <TextMessageModal
                isOpen={textModal.open}
                title={textModal.title}
                initialValue={textModal.value}
                onClose={() => setTextModal({ open: false, blockId: null, value: "", title: "Enter message" })}
                onSave={(nextValue) => {
                    if (!editableTarget || !textModal.blockId) {
                        setTextModal({ open: false, blockId: null, value: "", title: "Enter message" });
                        return;
                    }
                    const finalValue = (nextValue || "").trim() || "Hi";
                    dispatch(pushUndoState(histPayload));
                    if (editableTarget.type === "simulatorRobot") {
                        dispatch(updateSimulatorBlockMessage({
                            robotId: editableTarget.id,
                            blockId: textModal.blockId,
                            message: finalValue
                        }));
                    } else {
                        dispatch(updateBlockMessage({
                            actorId: editableTarget.id,
                            blockId: textModal.blockId,
                            message: finalValue
                        }));
                    }
                    setTextModal({ open: false, blockId: null, value: "", title: "Enter message" });
                }}
            />

            <TextMessageModal
                isOpen={scriptTextModal.open}
                title="Enter script commands"
                initialValue={scriptTextModal.value}
                placeholder={'move right 10\nwait 1\nsay "hello"'}
                rows={8}
                maxLength={600}
                onClose={() => setScriptTextModal({ open: false, blockId: null, value: "" })}
                onSave={(nextValue) => {
                    if (!editableTarget || !scriptTextModal.blockId) {
                        setScriptTextModal({ open: false, blockId: null, value: "" });
                        return;
                    }
                    const normalized = (nextValue || "").replace(/\r\n/g, "\n").trim();
                    dispatch(pushUndoState(histPayload));
                    const payload = {
                        blockId: scriptTextModal.blockId,
                        data: { scriptText: normalized }
                    };
                    if (editableTarget.type === "simulatorRobot") {
                        payload.robotId = editableTarget.id;
                    } else {
                        payload.actorId = editableTarget.id;
                    }
                    dispatch(updateBlockCustomData(payload));
                    setScriptTextModal({ open: false, blockId: null, value: "" });
                }}
            />

            {/* Pin configuration modal */}
            <PinConfigModal
                isOpen={showPinModal}
                onClose={() => setShowPinModal(false)}
                initialData={selectedPinBlock || {}}
                onSave={(data) => {
                    if (selectedPinBlock) {
                        // snapshot before change (so pin config is undoable)
                        dispatch(pushUndoState(histPayload));

                        const updatedBlock = {
                            ...selectedPinBlock,
                            pin: data.pin,
                            mode: data.mode,
                            state: data.state,
                        };

                        if (editableTarget.type === "simulatorRobot") {
                            dispatch(
                                updateSimulatorBlockCount({
                                    robotId: editableTarget.id,
                                    blockId: selectedPinBlock.id,
                                    newCount: 0,
                                    property: "pinConfig",
                                    value: data, // we store full config here
                                })
                            );
                        } else {
                            dispatch(
                                updateBlockCount({
                                    actorId: editableTarget.id,
                                    blockId: selectedPinBlock.id,
                                    newCount: 0,
                                    property: "pinConfig",
                                    value: data,
                                })
                            );
                        }

                        console.log("✅ Pin configured:", data);
                    }
                    setShowPinModal(false);
                }}
            />
        </div>
    );
} 
